<template>
  <div class="wf-editor" tabindex="0" @keydown="handleKeyDown">
    <header class="wf-header">
      <div class="wf-header-left">
        <input
          class="wf-name-input"
          type="text"
          v-model="workflowName"
          maxlength="255"
          placeholder="Workflow name"
        />
        <button @click="organizeFlow" class="wf-btn wf-btn-ghost">
          <Icon icon="tabler:layout-distribute-vertical" width="18" height="18" />
          {{ t('llmFlow.organizeFlowButton') }}
        </button>
        <button v-if="isElementSelected === true" @click="requestDeleteSelectedElements" class="wf-btn wf-btn-ghost wf-btn-danger">
          <Icon icon="tabler:trash" width="18" height="18" />
          {{ t('llmFlow.deleteButton') }}
        </button>
      </div>
      <div class="wf-header-right">
        <button @click="testWorkflow" class="wf-btn wf-btn-ghost" :disabled="isTestingWorkflow === true || isSavingWorkflow === true || isLoadingWorkflow === true">
          {{ isTestingWorkflow === true ? 'Testing...' : 'Test Flow' }}
        </button>
        <button @click="saveWorkflow" class="wf-btn" :disabled="isSavingWorkflow === true || isLoadingWorkflow === true">
          {{ isSavingWorkflow === true ? 'Saving...' : 'Save' }}
        </button>
      </div>
    </header>

    <div class="wf-body">
      <div v-if="isLoadingWorkflow === true" class="wf-editor-loading">
        <svg class="wf-editor-loading-spinner" viewBox="0 0 50 50" aria-hidden="true">
          <circle class="wf-editor-loading-track" cx="25" cy="25" r="20" fill="none" stroke-width="4"></circle>
          <circle class="wf-editor-loading-head" cx="25" cy="25" r="20" fill="none" stroke-width="4"></circle>
        </svg>
        <div class="wf-editor-loading-label">Loading workflow...</div>
      </div>
      <aside class="wf-sidebar">
        <div class="wf-sidebar-header">
          <div class="wf-sidebar-header-row">
            <span class="wf-sidebar-title">{{ isWorkflowChatOpen === true ? 'Assistant Chat' : t('workflowEditor.supportedNodesTitle', 'Nodes') }}</span>
            <div class="wf-sidebar-header-actions">
              <button
                class="wf-sidebar-mode-btn"
                :class="isWorkflowChatOpen === false ? 'wf-sidebar-mode-btn-active' : ''"
                @click="showWorkflowNodesPanel"
              >
                Nodes
              </button>
              <button
                class="wf-sidebar-mode-btn"
                :class="isWorkflowChatOpen === true ? 'wf-sidebar-mode-btn-active' : ''"
                @click="showWorkflowChatPanel"
              >
                Chat
              </button>
            </div>
          </div>
        </div>
        <template v-if="isWorkflowChatOpen === false">
          <WorkflowNodePalette
            :search-value="workflowNodeSearch"
            :filtered-groups="filteredSupportedNodeGroups"
            @update:searchValue="workflowNodeSearch = $event"
            @dragstart="onDragStart"
            @addnode="addSupportedNode"
          />
        <WorkflowNodeConfigPanel
          :selected-canvas-node="selectedCanvasNode"
          :available-agents="availableAgents"
          :available-tools-select-options="availableToolsSelectOptions"
          :workflow-variable-options="workflowVariableOptions"
          :workflow-conversation-options="workflowConversationOptions"
          :workflow-tag-options="workflowTagOptions"
          :workflow-guid="workflowGuid"
          :get-node-selected-agent-ids="getNodeSelectedAgentIds"
          :get-node-selected-conversation-variable-ids="getNodeSelectedConversationVariableIds"
          :update-selected-node-data="updateSelectedNodeData"
          :update-selected-node-config="updateSelectedNodeConfig"
          :update-selected-node-agent-ids-by-model="updateSelectedNodeAgentIdsByModel"
          :update-selected-node-conversation-variable-by-model="updateSelectedNodeConversationVariableByModel"
          :update-node-trigger-type="updateNodeTriggerType"
          :update-node-action-type="updateNodeActionType"
          :handle-select-activate-tool="handleSelectActivateTool"
          :update-header-key="updateHeaderKey"
          :update-header-value="updateHeaderValue"
          :remove-header="removeHeader"
          :add-header="addHeader"
        />
        </template>
        <template v-else>
          <WorkflowAssistantPanel
            :messages="workflowAssistantMessages"
            :is-loading="workflowAssistantIsLoading"
            :messages-ref="workflowChatMessagesRef"
            :model-value="workflowAssistantInput"
            :quick-prompts="workflowAssistantQuickPrompts"
            @update:modelValue="workflowAssistantInput = $event"
            @send="submitWorkflowAssistantPrompt"
            @quick="sendWorkflowAssistantQuickPrompt"
          />
        </template>
      </aside>
      <div class="wf-canvas-wrap">
      <VueFlow 
        :key="flowKey" 
        v-model="elements" 
        :fit-view-on-init="true"
        :min-zoom="0.1"
        :max-zoom="8"
        :default-zoom="1"
        :node-types="nodeTypes"
        @node-click="handleNodeClick"
        @node-double-click="handleNodeDoubleClick" 
        @edge-double-click="handleEdgeDoubleClick" 
        :select-nodes-on-drag="true"
        :zoom-on-scroll="true"
        :zoom-on-pinch="true"
        :zoom-on-double-click="true"
        :pan-on-drag="true"
        :delete-key-code="null" 
        @dragover.prevent="() => {}"
        @drop="onDrop"
        class="wf-canvas"
      >
        <Background />
        <Controls>
           <ControlButton :title="t('llmFlow.deleteSelectedButton')" @click="requestDeleteSelectedElements">
              <Icon icon="tabler:trash" width="16" height="16" />
          </ControlButton>
        </Controls>

     
      <template #node-root="props">
        <div class="node-card" :connectable="false"> 
          <div class="node-header header-trigger">
            <div class="node-header-icon">
              <Icon icon="tabler:flag" width="18" height="18" />
            </div>
            <div class="node-header-title">
              {{ (props.data.nodeType !== undefined && props.data.nodeType !== null && props.data.nodeType !== '') ? (props.data.nodeType === 'Root' ? 'Start Conversation' : props.data.nodeType) : 'Start Conversation' }}
            </div>
          </div>
          <div class="node-body" style="padding-bottom: 16px;">
            <div style="font-size: 0.85rem; font-weight: 500; color: #374151; text-align: center;">
              {{ (props.data.label !== undefined && props.data.label !== null && props.data.label !== '') ? props.data.label : 'We starts here!' }}
            </div>
          </div>
          <Handle type="source" :position="Position.Right" class="custom-right-handle" :style="{ top: '50%' }" />
        </div>
      </template>

      <template #node-default="props">
        <div class="node-card" :class="{ 'has-in-port': isNodeInPortVisible(props.id), 'trigger-node': props.data.nodeType === 'Trigger', 'logic-node': props.data.nodeType === 'If', 'action-node': props.data.nodeType === 'Action' }">
          <div class="node-id-badge">#{{ props.id }}</div>
          <div v-if="isNodeInPortVisible(props.id)" class="port-in-row">
            <Handle type="target" :position="Position.Left" class="custom-left-handle" />
          </div>
          
          <div class="node-body">
            <div class="node-title-area">
              <div class="node-icon-and-type">
                <Icon v-if="props.data.nodeType === 'Trigger'" icon="tabler:bolt" width="16" height="16" />
                <Icon v-else-if="props.data.nodeType === 'If'" icon="tabler:git-branch" width="16" height="16" />
                <Icon v-else-if="props.data.nodeType === 'Action'" icon="tabler:player-play" width="16" height="16" />
                <Icon v-else icon="tabler:puzzle" width="16" height="16" />
                <span class="node-type-label">{{ getCanvasNodeTypeLabel(props.data.nodeType) }}</span>
              </div>
              <div class="node-main-title">{{ props.data.label }}</div>
            </div>
            
            <div class="node-inline-row" v-if="(props.data.nodeType === 'Trigger' && ['onCallEnd', 'onCallAnalysis'].includes(props.data.triggerType) === true) || (props.data.nodeType === 'Action' && props.data.actionType !== 'sendConversation')" @mousedown.stop @click.stop>
              <MultiSelect
                :model-value="getNodeSelectedAgentIds(props.data)"
                @update:modelValue="value => updateNodeAgentIdsByModel(props.id, value)"
                :options="availableAgents"
                label="Run on Agents"
                placeholder="Select agents"
                id-field="agentid"
                label-field="agentname"
                :is-loading="availableAgents.length === 0"
                button-class="wf-agent-multiselect-btn"
              />
            </div>

            <div class="node-inline-row" v-if="props.data.nodeType === 'Action' && props.data.actionType === 'activateTool'">
              <label class="node-inline-label">Tool</label>
              <select
                class="node-inline-select"
                :value="props.data.config?.toolId"
                @mousedown.stop
                @click.stop
                @change="updateNodeActivateToolById(props.id, $event.target.value)"
              >
                <option value="">Select tool</option>
                <option v-for="tool in availableToolsSelectOptions" :key="`node-tool-option-${props.id}-${tool.id}`" :value="tool.id">{{ tool.label }}</option>
              </select>
            </div>

            <div v-if="props.data.nodeType === 'Trigger' && props.data.triggerType === 'onWebhook'" class="node-inline-row">
              <div class="text-xs text-primary font-mono truncate bg-base-200 p-1 rounded border border-base-300" v-if="workflowGuid">
                ...{{ workflowGuid.slice(-8) }}
              </div>
              <div v-else class="text-[10px] text-orange-500 italic">Save to generate URL</div>
            </div>

            <div v-if="props.data.nodeType === 'Trigger' && props.data.triggerType === 'onTimer'" class="node-inline-row">
              <label class="node-inline-label">Mode</label>
              <select
                class="node-inline-select"
                :value="props.data.config?.mode"
                @mousedown.stop
                @click.stop
                @change="updateNodeConfigById(props.id, { mode: $event.target.value })"
              >
                <option value="delay">delay</option>
                <option value="interval">interval</option>
              </select>
            </div>

            <div v-if="props.data.nodeType === 'Trigger' && props.data.triggerType === 'onTimer' && props.data.config?.mode === 'delay'" class="node-inline-row">
              <label class="node-inline-label">Delay Value</label>
              <input
                class="node-inline-input"
                type="number"
                min="1"
                :value="props.data.config?.delayValue"
                @mousedown.stop
                @click.stop
                @input="updateNodeConfigById(props.id, { delayValue: parsePositiveIntValue($event.target.value) })"
              />
            </div>

            <div v-if="props.data.nodeType === 'Trigger' && props.data.triggerType === 'onTimer' && props.data.config?.mode === 'delay'" class="node-inline-row">
              <label class="node-inline-label">Delay Unit</label>
              <select
                class="node-inline-select"
                :value="props.data.config?.delayUnit"
                @mousedown.stop
                @click.stop
                @change="updateNodeConfigById(props.id, { delayUnit: $event.target.value })"
              >
                <option value="seconds">seconds</option>
                <option value="minutes">minutes</option>
              </select>
            </div>

            <div v-if="props.data.nodeType === 'Trigger' && props.data.triggerType === 'onTimer' && props.data.config?.mode === 'interval'" class="node-inline-row">
              <label class="node-inline-label">Each Minutes</label>
              <input
                class="node-inline-input"
                type="number"
                min="1"
                :value="props.data.config?.intervalMinutes"
                @mousedown.stop
                @click.stop
                @input="updateNodeConfigById(props.id, { intervalMinutes: parsePositiveIntValue($event.target.value) })"
              />
            </div>

            <div v-if="props.data.nodeType === 'If'" class="node-inline-row">
              <label class="node-inline-label">Source</label>
              <select
                class="node-inline-select"
                :value="props.data.config?.sourceType || 'variable'"
                @mousedown.stop
                @click.stop
                @change="updateNodeConfigById(props.id, { sourceType: $event.target.value })"
              >
                <option value="variable">variable</option>
                <option value="conversation">conversation</option>
                <option value="tag">tag</option>
              </select>
            </div>
            <div v-if="props.data.nodeType === 'If' && (props.data.config?.sourceType || 'variable') === 'variable'" class="node-inline-row">
              <label class="node-inline-label">Variable</label>
              <select
                class="node-inline-select"
                :value="props.data.config?.sourceKey || ''"
                @mousedown.stop
                @click.stop
                @change="updateNodeConfigById(props.id, { sourceKey: $event.target.value })"
              >
                <option value="">Select variable</option>
                <option v-for="variableOption in workflowVariableOptions" :key="`node-if-variable-${props.id}-${variableOption.id}`" :value="variableOption.value">{{ variableOption.label }}</option>
              </select>
            </div>
            <div v-if="props.data.nodeType === 'If' && (props.data.config?.sourceType || 'variable') === 'conversation'" class="node-inline-row">
              <label class="node-inline-label">Conversation</label>
              <select
                class="node-inline-select"
                :value="props.data.config?.sourceKey || ''"
                @mousedown.stop
                @click.stop
                @change="updateNodeConfigById(props.id, { sourceKey: $event.target.value })"
              >
                <option value="">Select conversation source</option>
                <option v-for="conversationOption in workflowConversationOptions" :key="`node-if-conversation-${props.id}-${conversationOption.id}`" :value="conversationOption.value">{{ conversationOption.label }}</option>
              </select>
            </div>
            <div v-if="props.data.nodeType === 'If' && (props.data.config?.sourceType || 'variable') === 'tag'" class="node-inline-row">
              <label class="node-inline-label">Tag</label>
              <select
                class="node-inline-select"
                :value="props.data.config?.tagValue || ''"
                @mousedown.stop
                @click.stop
                @change="updateNodeConfigById(props.id, { tagValue: $event.target.value })"
              >
                <option value="">Select tag</option>
                <option v-for="tagOption in workflowTagOptions" :key="`node-if-tag-${props.id}-${tagOption.id}`" :value="tagOption.value">{{ tagOption.label }}</option>
              </select>
            </div>

            <div v-if="props.data.nodeType === 'If'" class="node-inline-row">
              <label class="node-inline-label">Operator</label>
              <select
                class="node-inline-select"
                :value="props.data.config?.operator"
                @mousedown.stop
                @click.stop
                @change="updateNodeConfigById(props.id, { operator: $event.target.value })"
              >
                <option value="equals">equals</option>
                <option value="notEquals">notEquals</option>
                <option value="contains">contains</option>
                <option value="greaterThan">greaterThan</option>
                <option value="lessThan">lessThan</option>
                <option value="exists">exists</option>
                <option value="isEmpty">isEmpty</option>
              </select>
            </div>

            <div v-if="props.data.nodeType === 'If'" class="node-inline-row">
              <label class="node-inline-label">Right</label>
              <input
                class="node-inline-input"
                :value="props.data.config?.right"
                @mousedown.stop
                @click.stop
                @input="updateNodeConfigById(props.id, { right: $event.target.value })"
              />
            </div>

            <div v-if="props.data.nodeType === 'Action' && props.data.actionType === 'activateTool'" class="node-inline-row">
              <label class="node-inline-label">Tool</label>
              <select
                class="node-inline-select"
                :value="props.data.config?.toolId"
                @mousedown.stop
                @click.stop
                @change="updateNodeActivateToolById(props.id, $event.target.value)"
              >
                <option value="">Select tool</option>
                <option v-for="tool in availableToolsSelectOptions" :key="`node-tool-option-${props.id}-${tool.id}`" :value="tool.id">{{ tool.label }}</option>
              </select>
            </div>

            <div v-if="props.data.nodeType === 'Action' && props.data.actionType === 'httpRequest'" class="node-inline-row">
              <label class="node-inline-label">Method</label>
              <select
                class="node-inline-select"
                :value="props.data.config?.method"
                @mousedown.stop
                @click.stop
                @change="updateNodeConfigById(props.id, { method: $event.target.value })"
              >
                <option value="GET">GET</option>
                <option value="POST">POST</option>
                <option value="PUT">UPDATE</option>
                <option value="PATCH">PATCH</option>
                <option value="DELETE">DELETE</option>
              </select>
            </div>

            <div v-if="props.data.nodeType === 'Action' && props.data.actionType === 'httpRequest'" class="node-inline-row">
              <label class="node-inline-label">URL</label>
              <input
                class="node-inline-input"
                :value="props.data.config?.url"
                placeholder="https://..."
                @mousedown.stop
                @click.stop
                @input="updateNodeConfigById(props.id, { url: $event.target.value })"
              />
              <div class="node-inline-hint">Variables supported: &#123;&#123;variableName&#125;&#125;</div>
            </div>

            <div v-if="props.data.nodeType === 'Action' && props.data.actionType === 'httpRequest'" class="node-inline-row">
              <label class="node-inline-label">Headers</label>
              <div v-for="(header, index) in (props.data.config?.headersList || [])" :key="'node-header-'+index" class="flex space-x-2 mb-2">
                <input class="node-inline-input flex-1" :value="header.key" @mousedown.stop @click.stop @input="updateHeaderKey(props.id, index, $event.target.value)" placeholder="Header-Name" />
                <input class="node-inline-input flex-1" :value="header.value" @mousedown.stop @click.stop @input="updateHeaderValue(props.id, index, $event.target.value)" placeholder="&#123;&#123;variableValue&#125;&#125;" />
                <button class="btn btn-xs btn-ghost text-red-500 p-1" @mousedown.stop @click.stop="removeHeader(props.id, index)">
                  <Icon icon="tabler:trash" width="14" />
                </button>
              </div>
              <button class="btn btn-xs btn-outline mt-1" @mousedown.stop @click.stop="addHeader(props.id)">+ Add Header</button>
              <div class="node-inline-hint">Header values support variables: &#123;&#123;variableName&#125;&#125;</div>
            </div>

            <div v-if="props.data.nodeType === 'Action' && props.data.actionType === 'httpRequest'" class="node-inline-row">
              <label class="node-inline-label">Body (JSON)</label>
              <div @mousedown.stop @click.stop class="monaco-container-wrapper">
                <MonacoEditor 
                  :modelValue="props.data.config?.body || '{}'" 
                  @update:modelValue="val => updateNodeConfigById(props.id, { body: val })" 
                  height="150px" 
                />
              </div>
              <div class="node-inline-hint">Use JSON with variables like &#123;&#123;customerId&#125;&#125;</div>
            </div>

            <div v-if="props.data.nodeType === 'Action' && props.data.actionType === 'sendEmail'" class="node-inline-row">
              <label class="node-inline-label">To</label>
              <input
                class="node-inline-input"
                :value="props.data.config?.to"
                @mousedown.stop
                @click.stop
                @input="updateNodeConfigById(props.id, { to: $event.target.value })"
              />
            </div>

            <div v-if="props.data.nodeType === 'Action' && props.data.actionType === 'sendEmail'" class="node-inline-row">
              <label class="node-inline-label">Provider</label>
              <input
                class="node-inline-input"
                :value="props.data.config?.provider"
                @mousedown.stop
                @click.stop
                @input="updateNodeConfigById(props.id, { provider: $event.target.value })"
              />
            </div>
            <div v-if="props.data.nodeType === 'Action' && props.data.actionType === 'sendSms'" class="node-inline-row">
              <label class="node-inline-label">To</label>
              <input
                class="node-inline-input"
                :value="props.data.config?.to"
                @mousedown.stop
                @click.stop
                @input="updateNodeConfigById(props.id, { to: $event.target.value })"
              />
            </div>
            <div v-if="props.data.nodeType === 'Action' && props.data.actionType === 'sendSms'" class="node-inline-row">
              <label class="node-inline-label">Message</label>
              <textarea
                class="node-inline-textarea"
                :value="props.data.config?.message"
                @mousedown.stop
                @click.stop
                @input="updateNodeConfigById(props.id, { message: $event.target.value })"
              ></textarea>
            </div>
            <div v-if="props.data.nodeType === 'Action' && props.data.actionType === 'sendSms'" class="node-inline-row">
              <label class="node-inline-label">Webhook URL</label>
              <input
                class="node-inline-input"
                :value="props.data.config?.webhookUrl"
                @mousedown.stop
                @click.stop
                @input="updateNodeConfigById(props.id, { webhookUrl: $event.target.value })"
              />
            </div>
            <div v-if="props.data.nodeType === 'Action' && props.data.actionType === 'queryLlm'" class="node-inline-row">
              <label class="node-inline-label">Prompt</label>
              <textarea
                class="node-inline-textarea"
                :value="props.data.config?.prompt"
                @mousedown.stop
                @click.stop
                @input="updateNodeConfigById(props.id, { prompt: $event.target.value })"
              ></textarea>
            </div>
            <div v-if="props.data.nodeType === 'Action' && props.data.actionType === 'queryLlm'" class="node-inline-row">
              <label class="node-inline-label">Webhook URL</label>
              <input
                class="node-inline-input"
                :value="props.data.config?.webhookUrl"
                @mousedown.stop
                @click.stop
                @input="updateNodeConfigById(props.id, { webhookUrl: $event.target.value })"
              />
            </div>
            <div v-if="props.data.nodeType === 'Action' && props.data.actionType === 'queryLlm'" class="node-inline-row">
              <label class="node-inline-label">Response Path</label>
              <input
                class="node-inline-input"
                :value="props.data.config?.responsePath"
                @mousedown.stop
                @click.stop
                @input="updateNodeConfigById(props.id, { responsePath: $event.target.value })"
              />
            </div>

            <div v-if="props.data.nodeType === 'Action' && props.data.actionType === 'executeJs'" class="node-inline-row">
              <label class="node-inline-label">Script</label>
              <textarea
                class="node-inline-textarea"
                :value="props.data.config?.script"
                @mousedown.stop
                @click.stop
                @input="updateNodeConfigById(props.id, { script: $event.target.value })"
              ></textarea>
            </div>
            <div v-if="props.data.nodeType === 'Action' && props.data.actionType === 'executeJs'" class="node-inline-row">
              <label class="node-inline-label">Use Variables in JS</label>
              <select
                class="node-inline-select"
                :value="props.data.config?.useVariables === true ? 'true' : 'false'"
                @mousedown.stop
                @click.stop
                @change="updateNodeConfigById(props.id, { useVariables: $event.target.value === 'true' })"
              >
                <option value="true">true</option>
                <option value="false">false</option>
              </select>
            </div>

            <div v-if="props.data.nodeType === 'Action' && props.data.actionType === 'sendConversation'" class="node-inline-row">
              <label class="node-inline-label">Webhook URL</label>
              <input
                class="node-inline-input"
                :value="props.data.config?.address"
                placeholder="https://..."
                @mousedown.stop
                @click.stop
                @input="updateNodeConfigById(props.id, { address: $event.target.value })"
              />
            </div>
            <div v-if="props.data.nodeType === 'Action' && props.data.actionType === 'sendConversation'" class="node-inline-row">
              <MultiSelect
                :model-value="getNodeSelectedConversationVariableIds(props.data)"
                @update:modelValue="value => updateNodeConversationVariableByModel(props.id, value)"
                :options="workflowVariableOptions"
                label="Conversation Variable"
                placeholder="Select variable"
                id-field="value"
                label-field="label"
                :is-loading="workflowVariableOptions.length === 0"
                button-class="wf-agent-multiselect-btn"
                :max-selections="1"
                force-direction="ltr"
              />
            </div>
            <div v-if="props.data.nodeType === 'Action' && props.data.actionType === 'manageVariable'" class="node-inline-row">
              <label class="node-inline-label">Operation</label>
              <select
                class="node-inline-select"
                :value="props.data.config?.operation || 'new'"
                @mousedown.stop
                @click.stop
                @change="updateNodeConfigById(props.id, { operation: $event.target.value })"
              >
                <option value="new">new</option>
                <option value="update">update</option>
                <option value="increase">increase</option>
                <option value="remove">remove</option>
              </select>
            </div>
            <div v-if="props.data.nodeType === 'Action' && props.data.actionType === 'manageVariable'" class="node-inline-row">
              <label class="node-inline-label">Variable Name</label>
              <input
                class="node-inline-input"
                :value="props.data.config?.variableName"
                placeholder="myVar"
                @mousedown.stop
                @click.stop
                @input="updateNodeConfigById(props.id, { variableName: $event.target.value })"
              />
            </div>
            <div v-if="props.data.nodeType === 'Action' && props.data.actionType === 'manageVariable' && props.data.config?.operation !== 'remove' && props.data.config?.operation !== 'increase'" class="node-inline-row">
              <label class="node-inline-label">Type</label>
              <select
                class="node-inline-select"
                :value="props.data.config?.valueType || 'string'"
                @mousedown.stop
                @click.stop
                @change="updateNodeConfigById(props.id, { valueType: $event.target.value })"
              >
                <option value="string">string</option>
                <option value="number">number</option>
                <option value="conversation">conversation</option>
              </select>
            </div>
            <div v-if="props.data.nodeType === 'Action' && props.data.actionType === 'manageVariable' && props.data.config?.operation === 'increase'" class="node-inline-row">
              <label class="node-inline-label">Increase By</label>
              <input
                class="node-inline-input"
                type="number"
                :value="props.data.config?.amount ?? 1"
                @mousedown.stop
                @click.stop
                @input="updateNodeConfigById(props.id, { amount: $event.target.value })"
              />
            </div>
            <div v-if="props.data.nodeType === 'Action' && props.data.actionType === 'manageVariable' && (props.data.config?.operation === 'new' || props.data.config?.operation === 'update') && props.data.config?.valueType !== 'conversation'" class="node-inline-row">
              <label class="node-inline-label">Value</label>
              <input
                class="node-inline-input"
                :value="props.data.config?.value"
                @mousedown.stop
                @click.stop
                @input="updateNodeConfigById(props.id, { value: $event.target.value })"
              />
            </div>
            <div v-if="props.data.nodeType === 'Action' && props.data.actionType === 'manageVariable' && (props.data.config?.operation === 'new' || props.data.config?.operation === 'update') && props.data.config?.valueType === 'conversation'" class="node-inline-row">
              <label class="node-inline-label">From Variable</label>
              <select
                class="node-inline-select"
                :value="props.data.config?.fromVariable || ''"
                @mousedown.stop
                @click.stop
                @change="updateNodeConfigById(props.id, { fromVariable: $event.target.value })"
              >
                <option value="">Select conversation variable</option>
                <option v-for="variableOption in workflowVariableOptions" :key="`node-manage-variable-source-${props.id}-${variableOption.id}`" :value="variableOption.value">{{ variableOption.label }}</option>
              </select>
            </div>
          </div>

          <div class="port-out-row">
            <template v-if="props.data.nodeType === 'If'">
              <div class="port-out-item port-out-item-half if-line-port">
                <span class="port-label-bottom">true</span>
                <Handle id="true" type="source" :position="Position.Right" class="custom-right-handle" :style="{ top: '50%' }" />
              </div>
              <div class="port-out-item port-out-item-half if-line-port">
                <span class="port-label-bottom">false</span>
                <Handle id="false" type="source" :position="Position.Right" class="custom-right-handle" :style="{ top: '50%' }" />
              </div>
            </template>
            <template v-else-if="props.data.nodeType === 'Action'">
              <div class="port-out-item port-out-item-half">
                <span class="port-label-bottom">success</span>
                <Handle id="success" type="source" :position="Position.Right" class="custom-right-handle" :style="{ top: '50%' }" />
              </div>
              <div class="port-out-item port-out-item-half">
                <span class="port-label-bottom">error</span>
                <Handle id="error" type="source" :position="Position.Right" class="custom-right-handle" :style="{ top: '50%' }" />
              </div>
            </template>
            <template v-else>
              <div class="port-out-item port-out-item-full">
                <span class="port-label-bottom">out</span>
                <Handle type="source" :position="Position.Right" class="custom-right-handle" :style="{ top: '50%' }" />
              </div>
            </template>
          </div>
        </div>
      </template>

      <template #edge-smoothstep="props">
        <SmoothStepEdge 
           :key="props.id + '-' + props.label" 
           :id="props.id" 
           :source-x="props.sourceX" 
           :source-y="props.sourceY" 
           :target-x="props.targetX" 
           :target-y="props.targetY" 
           :source-position="props.sourcePosition" 
           :target-position="props.targetPosition" 
           :label="props.label" 
           :markerEnd="props.markerEnd"
           :style="props.style"
        />
        
        <EdgeLabelRenderer>
          <div
            v-if="props.label !== undefined && props.label !== null && props.label.trim().length > 0"
            :key="props.id + '-' + (props.label !== undefined && props.label !== null && props.label !== '' ? props.label : 'empty') + '-labeldiv'" 
            :style="{
              position: 'absolute',
              transform: getEdgeLabelPosition(props),
              pointerEvents: 'all',
              zIndex: 20 
            }"
            :class="['edge-condition-card']"
            @dblclick.stop="handleEdgeDoubleClick({ edge: props, event: $event })" 
          >
            {{ props.label }}
                </div>
          <div
            v-else 
            :key="props.id + '-empty-labeldiv'" 
             :style="{
              position: 'absolute',
              transform: getEdgeLabelPosition(props),
              pointerEvents: 'all',
              zIndex: 20 
            }"
            class="edge-condition-card no-condition" 
             @dblclick.stop="handleEdgeDoubleClick({ edge: props, event: $event })" 
          >
            {{ t('llmFlow.noConditionSet') }}
                </div>
        </EdgeLabelRenderer>
      </template>

      </VueFlow>
      </div>
    </div>
    

    <StepEditorModal 
      v-if="isStepModalOpen === true" 
      :is-open="isStepModalOpen === true"
      :node-data="editingNodeData"
      :available-tools="availableTools"
      :all-flow-nodes="elements.filter(el => el.position !== undefined && el.position !== null)"
      @close="closeStepModal"
      @save="saveStepData"
    />

    <ActionStepEditorModal
      v-if="isActionStepModalOpen === true"
      :is-open="isActionStepModalOpen !== undefined && isActionStepModalOpen !== null && isActionStepModalOpen === true" 
      :node-data="editingNodeData"
      :available-tools="availableTools"
      :all-flow-nodes="elements.filter(el => el.position !== undefined && el.position !== null)"
      @close="closeActionStepModal" 
      @save="saveStepData"
    />

    <EdgeConditionModal
        :is-open="isEdgeModalOpen !== undefined && isEdgeModalOpen !== null && isEdgeModalOpen === true" 
        :condition="currentEdgeCondition"
        @close="closeEdgeModal"
        @save="saveEdgeCondition"
        @delete-edge="handleDeleteEdgeFromModal"
    />

    <dialog :open="isDeleteConfirmOpen !== undefined && isDeleteConfirmOpen !== null && isDeleteConfirmOpen === true" class="modal">
       <div class="modal-box">
            <h3 class="font-bold text-lg">{{ t('llmFlow.confirmDeletionTitle') }}</h3>
            <p class="py-4">{{ t('llmFlow.confirmDeletionMessage', { nodes: elementsToDelete.nodes.length, connections: elementsToDelete.edges.length }, `Are you sure you want to delete the selected ${elementsToDelete.nodes.length} step(s) and ${elementsToDelete.edges.length} connection(s)?`) }}</p>
            <div class="modal-action">
                <button class="btn mr-2" @click="cancelDelete">{{ t('llmFlow.cancelButton') }}</button>
                <button class="btn btn-error ml-2" @click="confirmDelete">{{ t('llmFlow.deleteButton') }}</button>
          </div>
        </div>
         <form method="dialog" class="modal-backdrop">
            <button @click="cancelDelete">{{ t('llmFlow.closeButton') }}</button>
        </form>
    </dialog>

  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch, nextTick, defineExpose, shallowRef } from 'vue';
import { Icon } from '@iconify/vue/dist/offline';
import { VueFlow, useVueFlow, SmoothStepEdge, EdgeLabelRenderer, Handle, Position, MarkerType, getSmoothStepPath } from '@vue-flow/core';
import { Background } from '@vue-flow/background';
import { Controls, ControlButton } from '@vue-flow/controls';
import StepEditorModal from './StepEditorModal.vue';
import ActionStepEditorModal from './ActionStepEditorModal.vue';
import EdgeConditionModal from './EdgeConditionModal.vue'; 
import axios from '../api/axiosInstance';
import { globalState } from '@/globalState';
import QuestionNode from './nodes/QuestionNode.vue';
import ActionNode from './nodes/ActionNode.vue';
import RootNode from './nodes/RootNode.vue';
import KnowledgeBaseNode from './nodes/KnowledgeBaseNode.vue';
import MonacoEditor from './MonacoEditor.vue';
import MultiSelect from './MultiSelect.vue';
import WorkflowNodePalette from './workflowEditor/WorkflowNodePalette.vue';
import WorkflowAssistantPanel from './workflowEditor/WorkflowAssistantPanel.vue';
import WorkflowNodeConfigPanel from './workflowEditor/WorkflowNodeConfigPanel.vue';
import { useI18n } from '@/i18n';
import { getAllAIChats } from '@/shared/services/aiChatsService';
import ItModal from '@/views/components/modal.vue';
import { useRoute, useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import { marked } from 'marked';
import { sanitizeHtml } from '@/shared/utils/sanitizeHtml';

import '@vue-flow/core/dist/style.css';
import '@vue-flow/core/dist/theme-default.css';
import '@vue-flow/controls/dist/style.css';

const { 
  addNodes, 
  onConnect, 
  addEdges, 
  getNode, 
  updateNode, 
  findNode, 
  removeNodes, 
  removeEdges, 
  getSelectedElements, 
  getSelectedNodes, 
  getSelectedEdges,
  findEdge,     
  setNodes,
  setEdges,
  addSelectedNodes,
  addSelectedEdges,
  fitView,
  project
} = useVueFlow();
const apiEndpoint = computed(() => globalState.apiEndpoint);
const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const toast = useToast();

let id = 0;
const elements = ref([]); 
const isStepModalOpen = ref(false);
const isActionStepModalOpen = ref(false);
const editingNodeId = ref(null);
const editingNodeData = ref({}); 
const availableTools = ref([]);
const availableAgents = ref([]);
const availableTags = ref([]);
const isDeleteConfirmOpen = ref(false);
const elementsToDelete = ref({ nodes: [], edges: [] });
const isEdgeModalOpen = ref(false); 
const editingEdgeId = ref(null);    
const currentEdgeCondition = ref('');
const flowKey = ref(0);
const workflowId = ref(null);
const workflowGuid = ref(null);
const workflowName = ref('');
const isSavingWorkflow = ref(false);
const isLoadingWorkflow = ref(false);
const isTestingWorkflow = ref(false);

const dragData = ref(null);
const isWorkflowChatOpen = ref(false);
const workflowAssistantInput = ref('');
const workflowAssistantMessages = ref([]);
const workflowAssistantIsLoading = ref(false);
const workflowAssistantSessionId = ref(null);
const workflowChatMessagesRef = ref(null);
let workflowAssistantMessageId = 1;
const workflowAssistantQuickPrompts = [
  'Send closed conversation to webhook',
  'Set HTTP request method to POST',
  'Set webhook URL',
  'Connect trigger to send conversation',
  'Rename selected node'
];

const nodeTypes = shallowRef({
  Question: QuestionNode,
  Action: ActionNode,
  Root: RootNode,
  KnowledgeBase: KnowledgeBaseNode,
});

const supportedWorkflowNodeGroups = [
  {
    id: 'triggers',
    label: 'Triggers',
    nodes: [
      {
        id: 'trigger-on-call-end',
        type: 'default',
        typeLabel: 'Trigger',
        icon: 'tabler:phone-off',
        label: 'On Call End',
        description: 'Starts workflow when a call ends.',
        data: { nodeType: 'Trigger', triggerType: 'onCallEnd', label: 'On Call End' }
      },
      {
        id: 'trigger-on-call-analysis',
        type: 'default',
        typeLabel: 'Trigger',
        icon: 'tabler:chart-bar',
        label: 'On Call Analysis',
        description: 'Starts workflow on call analysis completion.',
        data: { nodeType: 'Trigger', triggerType: 'onCallAnalysis', label: 'On Call Analysis' }
      },
      {
        id: 'trigger-on-webhook',
        type: 'default',
        typeLabel: 'Trigger',
        icon: 'tabler:webhook',
        label: 'On Webhook',
        description: 'Starts workflow when webhook is called.',
        data: { nodeType: 'Trigger', triggerType: 'onWebhook', label: 'On Webhook' }
      },
      {
        id: 'trigger-on-timer',
        type: 'default',
        typeLabel: 'Trigger',
        icon: 'tabler:clock',
        label: 'On Timer',
        description: 'Starts workflow by timer delay or interval.',
        data: {
          nodeType: 'Trigger',
          triggerType: 'onTimer',
          label: 'On Timer',
          config: {
            mode: 'interval',
            delayValue: 30,
            delayUnit: 'seconds',
            intervalMinutes: 5
          }
        }
      }
    ]
  },
  {
    id: 'logic',
    label: 'Logic',
    nodes: [
      {
        id: 'if-condition',
        type: 'default',
        typeLabel: 'If',
        icon: 'tabler:git-branch',
        label: 'If Condition',
        description: 'Route flow by condition true or false.',
        data: { nodeType: 'If', label: 'If Condition', config: { sourceType: 'variable', sourceKey: '', tagValue: '', operator: 'equals', left: '', right: '' } }
      }
    ]
  },
  {
    id: 'actions',
    label: 'Actions',
    nodes: [
      {
        id: 'action-activate-tool',
        type: 'default',
        typeLabel: 'Action',
        icon: 'tabler:tool',
        label: 'Activate Tool',
        description: 'Run an existing AI tool by toolId.',
        data: { nodeType: 'Action', actionType: 'activateTool', label: 'Activate Tool', config: { toolId: '', paramsMapping: {}, outputVariable: '' } }
      },
      {
        id: 'action-http-request',
        type: 'default',
        typeLabel: 'Action',
        icon: 'tabler:world-www',
        label: 'HTTP Request',
        description: 'Call external HTTP endpoint.',
        data: { nodeType: 'Action', actionType: 'httpRequest', label: 'HTTP Request', config: { method: 'POST', url: '', headersList: [], body: '{}', outputVariable: '' } }
      },
      {
        id: 'action-send-email',
        type: 'default',
        typeLabel: 'Action',
        icon: 'tabler:mail',
        label: 'Send Email',
        description: 'Send email via Microsoft365 or webhook.',
        data: { nodeType: 'Action', actionType: 'sendEmail', label: 'Send Email', config: { provider: 'microsoft365', to: '', subject: '', body: '', outputVariable: '' } }
      },
      {
        id: 'action-send-sms',
        type: 'default',
        typeLabel: 'Action',
        icon: 'tabler:message-circle',
        label: 'Send SMS',
        description: 'Send SMS using webhook provider.',
        data: { nodeType: 'Action', actionType: 'sendSms', label: 'Send SMS', config: { provider: 'webhook', to: '', message: '', webhookUrl: '', outputVariable: '' } }
      },
      {
        id: 'action-query-llm',
        type: 'default',
        typeLabel: 'Action',
        icon: 'tabler:sparkles',
        label: 'Query LLM',
        description: 'Call LLM endpoint and save response to variable.',
        data: { nodeType: 'Action', actionType: 'queryLlm', label: 'Query LLM', config: { provider: 'webhook', prompt: '', webhookUrl: '', responsePath: '', outputVariable: '' } }
      },
      {
        id: 'action-execute-js',
        type: 'default',
        typeLabel: 'Action',
        icon: 'tabler:code',
        label: 'Execute JS',
        description: 'Execute JavaScript in sandbox.',
        data: { nodeType: 'Action', actionType: 'executeJs', label: 'Execute JS', config: { script: '', paramsMapping: {}, outputVariable: '' } }
      },
      {
        id: 'action-send-conversation',
        type: 'default',
        typeLabel: 'Action',
        icon: 'tabler:message',
        label: 'Send Conversation',
        description: 'Send conversation as JSON to address.',
        data: { nodeType: 'Action', actionType: 'sendConversation', label: 'Send Conversation', config: { address: '', conversationVariable: '' } }
      },
      {
        id: 'action-manage-variable',
        type: 'default',
        typeLabel: 'Action',
        icon: 'tabler:variable',
        label: 'Manage Variable',
        description: 'Create, update, increase or remove variable.',
        data: { nodeType: 'Action', actionType: 'manageVariable', label: 'Manage Variable', config: { operation: 'new', variableName: '', valueType: 'string', value: '', amount: 1, fromVariable: '', outputVariable: '' } }
      }
    ]
  }
];
const integrationNodeGroups = computed(() => {
  const groupsMap = new Map();
  const toolsArray = Array.isArray(availableTools.value) === true ? availableTools.value : [];
  for (let i = 0; i < toolsArray.length; i = i + 1) {
    const tool = toolsArray[i];
    if (tool === undefined || tool === null) {
      continue;
    }
    const toolDataRaw = tool.toolData !== undefined ? tool.toolData : tool.tooldata;
    let toolData = {};
    if (toolDataRaw !== undefined && toolDataRaw !== null && typeof toolDataRaw === 'object') {
      toolData = toolDataRaw;
    }
    if (typeof toolDataRaw === 'string') {
      try {
        toolData = JSON.parse(toolDataRaw);
      } catch (error) {
        toolData = {};
      }
    }
    const integrationNameRaw = toolData.integrationName !== undefined ? toolData.integrationName : toolData.providerName;
    const integrationName = integrationNameRaw !== undefined && integrationNameRaw !== null && String(integrationNameRaw).trim().length > 0 ? String(integrationNameRaw).trim() : 'Tools';
    const groupId = `integration-${integrationName.toLowerCase().replace(/\s+/g, '-')}`;
    if (groupsMap.has(groupId) !== true) {
      groupsMap.set(groupId, {
        id: groupId,
        label: integrationName,
        nodes: []
      });
    }
    const rawToolId = tool.toolId !== undefined ? tool.toolId : tool.toolid;
    const parsedToolId = Number.parseInt(String(rawToolId), 10);
    if (Number.isFinite(parsedToolId) !== true || parsedToolId <= 0) {
      continue;
    }
    const imageRaw = toolData.integrationImage !== undefined ? toolData.integrationImage : (toolData.image !== undefined ? toolData.image : toolData.logoUrl);
    const toolNameRaw = tool.toolName !== undefined ? tool.toolName : tool.toolname;
    const toolName = toolNameRaw !== undefined && toolNameRaw !== null && String(toolNameRaw).trim().length > 0 ? String(toolNameRaw).trim() : `Tool ${parsedToolId}`;
    const nodeDef = {
      id: `integration-tool-${parsedToolId}`,
      type: 'default',
      typeLabel: 'Integration',
      icon: 'tabler:puzzle',
      label: toolName,
      image: imageRaw !== undefined && imageRaw !== null ? String(imageRaw) : '',
      description: `Activate ${toolName}`,
      data: {
        nodeType: 'Action',
        actionType: 'activateTool',
        label: toolName,
        config: {
          toolId: parsedToolId,
          paramsMapping: {},
          outputVariable: ''
        }
      }
    };
    groupsMap.get(groupId).nodes.push(nodeDef);
  }
  return Array.from(groupsMap.values());
});

const allSupportedNodeGroups = computed(() => {
  return [...supportedWorkflowNodeGroups, ...integrationNodeGroups.value];
});
const workflowNodeSearch = ref('');
const filteredSupportedNodeGroups = computed(() => {
  const searchValue = String(workflowNodeSearch.value !== undefined && workflowNodeSearch.value !== null ? workflowNodeSearch.value : '').trim().toLowerCase();
  if (searchValue.length === 0) {
    return allSupportedNodeGroups.value;
  }
  const groups = [];
  for (let i = 0; i < allSupportedNodeGroups.value.length; i = i + 1) {
    const group = allSupportedNodeGroups.value[i];
    if (group === undefined || group === null) {
      continue;
    }
    const groupLabel = String(group.label !== undefined && group.label !== null ? group.label : '').toLowerCase();
    const nodes = Array.isArray(group.nodes) === true ? group.nodes : [];
    const matchedNodes = [];
    for (let j = 0; j < nodes.length; j = j + 1) {
      const node = nodes[j];
      if (node === undefined || node === null) {
        continue;
      }
      const label = String(node.label !== undefined && node.label !== null ? node.label : '').toLowerCase();
      const description = String(node.description !== undefined && node.description !== null ? node.description : '').toLowerCase();
      const typeLabel = String(node.typeLabel !== undefined && node.typeLabel !== null ? node.typeLabel : '').toLowerCase();
      if (label.includes(searchValue) === true || description.includes(searchValue) === true || typeLabel.includes(searchValue) === true || groupLabel.includes(searchValue) === true) {
        matchedNodes.push(node);
      }
    }
    if (matchedNodes.length > 0) {
      groups.push({ ...group, nodes: matchedNodes });
    }
  }
  return groups;
});

const supportedWorkflowNodes = computed(() => {
  return allSupportedNodeGroups.value.flatMap(group => group.nodes);
});

const isElementSelected = computed(() => {
    return getSelectedNodes.value.length > 0 || getSelectedEdges.value.length > 0;
});
const selectedCanvasNodeId = ref(null);
const selectedCanvasNode = computed(() => {
  if (selectedCanvasNodeId.value === null || selectedCanvasNodeId.value === undefined) {
    return null;
  }
  const node = elements.value.find(el => el.position !== undefined && el.position !== null && el.id === selectedCanvasNodeId.value);
  if (node === undefined || node === null) {
    return null;
  }
  return node;
});
const firstCanvasNodeId = computed(() => {
  const nodes = elements.value.filter(el => el.position !== undefined && el.position !== null);
  if (nodes.length === 0) {
    return null;
  }
  return nodes[0].id;
});
const availableToolsSelectOptions = computed(() => {
  const tools = Array.isArray(availableTools.value) === true ? availableTools.value : [];
  const options = [];
  for (let i = 0; i < tools.length; i = i + 1) {
    const tool = tools[i];
    if (tool === undefined || tool === null) {
      continue;
    }
    const idRaw = tool.toolId !== undefined ? tool.toolId : tool.toolid;
    const id = Number.parseInt(String(idRaw), 10);
    if (Number.isFinite(id) !== true || id <= 0) {
      continue;
    }
    const nameRaw = tool.toolName !== undefined ? tool.toolName : tool.toolname;
    const name = nameRaw !== undefined && nameRaw !== null && String(nameRaw).trim().length > 0 ? String(nameRaw).trim() : `Tool ${id}`;
    options.push({ id, label: name });
  }
  return options;
});

function getDefaultOutputVariableForNode(node) {
  if (node === undefined || node === null) {
    return '';
  }
  const nodeType = node.data !== undefined && node.data !== null ? String(node.data.nodeType !== undefined ? node.data.nodeType : '') : '';
  if (nodeType === 'If') {
    return `${String(node.id)}_condition`;
  }
  if (nodeType !== 'Action') {
    return '';
  }
  const actionType = node.data !== undefined && node.data !== null ? String(node.data.actionType !== undefined ? node.data.actionType : '') : '';
  if (actionType === 'httpRequest') {
    return `${String(node.id)}_http`;
  }
  if (actionType === 'sendEmail') {
    return `${String(node.id)}_email`;
  }
  if (actionType === 'sendSms') {
    return `${String(node.id)}_sms`;
  }
  if (actionType === 'queryLlm') {
    return `${String(node.id)}_llm`;
  }
  if (actionType === 'activateTool') {
    return `${String(node.id)}_tool`;
  }
  if (actionType === 'executeJs') {
    return `${String(node.id)}_js`;
  }
  if (actionType === 'sendConversation') {
    return `${String(node.id)}_sendConversation`;
  }
  if (actionType === 'manageVariable') {
    return `${String(node.id)}_variable`;
  }
  return '';
}

const workflowVariableOptions = computed(() => {
  const variablesMap = new Map();
  variablesMap.set('payload', { id: 'payload', value: 'payload', label: 'Conversation payload (trigger)' });
  variablesMap.set('workflowId', { id: 'workflowId', value: 'workflowId', label: 'workflowId' });
  variablesMap.set('workflowGuid', { id: 'workflowGuid', value: 'workflowGuid', label: 'workflowGuid' });
  variablesMap.set('triggerType', { id: 'triggerType', value: 'triggerType', label: 'triggerType' });
  variablesMap.set('nowIso', { id: 'nowIso', value: 'nowIso', label: 'nowIso' });
  const nodes = elements.value.filter(el => el.position !== undefined && el.position !== null);
  for (let i = 0; i < nodes.length; i = i + 1) {
    const node = nodes[i];
    const nodeId = String(node.id !== undefined && node.id !== null ? node.id : '').trim();
    if (nodeId.length === 0) {
      continue;
    }
    const nodeType = node.data !== undefined && node.data !== null ? String(node.data.nodeType !== undefined ? node.data.nodeType : '') : '';
    if (nodeType === 'Trigger') {
      variablesMap.set(`payload_from_${nodeId}`, { id: `payload_from_${nodeId}`, value: 'payload', label: `Conversation from #${nodeId}` });
    }
    const config = node.data !== undefined && node.data !== null && node.data.config !== undefined && node.data.config !== null ? node.data.config : {};
    const configuredOutput = String(config.outputVariable !== undefined && config.outputVariable !== null ? config.outputVariable : '').trim();
    if (configuredOutput.length > 0) {
      variablesMap.set(`configured_${configuredOutput}_${nodeId}`, { id: `configured_${configuredOutput}_${nodeId}`, value: configuredOutput, label: `${configuredOutput} from #${nodeId}` });
    }
    if (nodeType === 'Action' && String(node.data.actionType !== undefined ? node.data.actionType : '') === 'manageVariable') {
      const variableName = String(config.variableName !== undefined && config.variableName !== null ? config.variableName : '').trim();
      if (variableName.length > 0) {
        variablesMap.set(`managed_${variableName}_${nodeId}`, { id: `managed_${variableName}_${nodeId}`, value: variableName, label: `${variableName} from #${nodeId}` });
      }
    }
    const defaultOutput = getDefaultOutputVariableForNode(node);
    if (defaultOutput.length > 0) {
      variablesMap.set(`default_${defaultOutput}_${nodeId}`, { id: `default_${defaultOutput}_${nodeId}`, value: defaultOutput, label: `${defaultOutput} from #${nodeId}` });
    }
  }
  return Array.from(variablesMap.values());
});

const workflowConversationOptions = computed(() => {
  return workflowVariableOptions.value.filter((option) => {
    const label = String(option !== undefined && option !== null && option.label !== undefined && option.label !== null ? option.label : '').toLowerCase();
    const value = String(option !== undefined && option !== null && option.value !== undefined && option.value !== null ? option.value : '').toLowerCase();
    return label.includes('conversation') === true || value.includes('payload') === true;
  });
});

const workflowTagOptions = computed(() => {
  const tagsMap = new Map();
  const rawTags = Array.isArray(availableTags.value) === true ? availableTags.value : [];
  for (let i = 0; i < rawTags.length; i = i + 1) {
    const tagItem = rawTags[i];
    let tagName = '';
    if (typeof tagItem === 'string') {
      tagName = tagItem.trim();
    } else if (tagItem !== undefined && tagItem !== null && typeof tagItem === 'object') {
      const candidate = tagItem.tagName !== undefined ? tagItem.tagName : (tagItem.tagname !== undefined ? tagItem.tagname : (tagItem.name !== undefined ? tagItem.name : tagItem.value));
      tagName = String(candidate !== undefined && candidate !== null ? candidate : '').trim();
    }
    if (tagName.length > 0) {
      tagsMap.set(tagName, { id: `tag-${tagName}`, value: tagName, label: tagName });
    }
  }
  return Array.from(tagsMap.values());
});

function getWorkflowNodeById(nodeId) {
  return supportedWorkflowNodes.value.find(node => node.id === nodeId);
}

function getCanvasNodeTypeLabel(nodeType) {
  if (nodeType === undefined || nodeType === null) {
    return 'Node';
  }
  if (nodeType === 'Trigger') {
    return 'Trigger';
  }
  if (nodeType === 'If') {
    return 'Condition';
  }
  if (nodeType === 'Action') {
    return 'Action';
  }
  return String(nodeType);
}

function getToolLabelById(rawToolId) {
  const parsed = Number.parseInt(String(rawToolId), 10);
  if (Number.isFinite(parsed) !== true || parsed <= 0) {
    return '';
  }
  const toolOption = availableToolsSelectOptions.value.find(item => item.id === parsed);
  if (toolOption === undefined || toolOption === null) {
    return '';
  }
  return toolOption.label;
}

function getNodeVisibleFields(nodeData) {
  if (nodeData === undefined || nodeData === null) {
    return [];
  }
  const fields = [];
  if (nodeData.nodeType === 'Trigger') {
    const triggerType = nodeData.triggerType !== undefined && nodeData.triggerType !== null ? String(nodeData.triggerType) : '';
    if (triggerType.length > 0) {
      fields.push({ key: 'trigger', value: triggerType });
    }
    if (triggerType === 'onTimer') {
      const cfg = nodeData.config !== undefined && nodeData.config !== null ? nodeData.config : {};
      const mode = cfg.mode !== undefined && cfg.mode !== null ? String(cfg.mode) : '';
      if (mode.length > 0) {
        fields.push({ key: 'mode', value: mode });
      }
      if (mode === 'delay') {
        fields.push({ key: 'value', value: String(cfg.delayValue !== undefined ? cfg.delayValue : '') });
        fields.push({ key: 'unit', value: String(cfg.delayUnit !== undefined ? cfg.delayUnit : '') });
      }
      if (mode === 'interval') {
        fields.push({ key: 'each', value: `${String(cfg.intervalMinutes !== undefined ? cfg.intervalMinutes : '')} minutes` });
      }
    }
    return fields;
  }
  if (nodeData.nodeType === 'If') {
    const cfg = nodeData.config !== undefined && nodeData.config !== null ? nodeData.config : {};
    fields.push({ key: 'source', value: String(cfg.sourceType !== undefined ? cfg.sourceType : 'variable') });
    fields.push({ key: 'field', value: String(cfg.sourceKey !== undefined && String(cfg.sourceKey).length > 0 ? cfg.sourceKey : (cfg.tagValue !== undefined ? cfg.tagValue : '')) });
    fields.push({ key: 'op', value: String(cfg.operator !== undefined ? cfg.operator : '') });
    fields.push({ key: 'right', value: String(cfg.right !== undefined ? cfg.right : '') });
    return fields;
  }
  if (nodeData.nodeType === 'Action') {
    const actionType = nodeData.actionType !== undefined && nodeData.actionType !== null ? String(nodeData.actionType) : '';
    fields.push({ key: 'action', value: actionType });
    const cfg = nodeData.config !== undefined && nodeData.config !== null ? nodeData.config : {};
    if (actionType === 'activateTool') {
      fields.push({ key: 'toolId', value: String(cfg.toolId !== undefined ? cfg.toolId : '') });
      const toolLabel = getToolLabelById(cfg.toolId);
      if (toolLabel.length > 0) {
        fields.push({ key: 'tool', value: toolLabel });
      }
    }
    if (actionType === 'httpRequest') {
      fields.push({ key: 'method', value: String(cfg.method !== undefined ? cfg.method : '') });
      fields.push({ key: 'url', value: String(cfg.url !== undefined ? cfg.url : '') });
    }
    if (actionType === 'sendEmail') {
      fields.push({ key: 'to', value: String(cfg.to !== undefined ? cfg.to : '') });
      fields.push({ key: 'provider', value: String(cfg.provider !== undefined ? cfg.provider : '') });
    }
    if (actionType === 'sendSms') {
      fields.push({ key: 'to', value: String(cfg.to !== undefined ? cfg.to : '') });
      fields.push({ key: 'provider', value: String(cfg.provider !== undefined ? cfg.provider : '') });
    }
    if (actionType === 'queryLlm') {
      fields.push({ key: 'provider', value: String(cfg.provider !== undefined ? cfg.provider : '') });
      fields.push({ key: 'responsePath', value: String(cfg.responsePath !== undefined ? cfg.responsePath : '') });
    }
    if (actionType === 'executeJs') {
      fields.push({ key: 'script', value: String(cfg.script !== undefined ? cfg.script : '') });
    }
    if (actionType === 'sendConversation') {
      fields.push({ key: 'address', value: String(cfg.address !== undefined ? cfg.address : '') });
    }
    if (actionType === 'manageVariable') {
      fields.push({ key: 'operation', value: String(cfg.operation !== undefined ? cfg.operation : '') });
      fields.push({ key: 'variable', value: String(cfg.variableName !== undefined ? cfg.variableName : '') });
      fields.push({ key: 'type', value: String(cfg.valueType !== undefined ? cfg.valueType : '') });
    }
    return fields;
  }
  return fields;
}

function handleNodeClick(event) {
  if (event === undefined || event === null) {
    return;
  }
  if (event.node === undefined || event.node === null) {
    return;
  }
  selectedCanvasNodeId.value = event.node.id;
}

function parsePositiveIntValue(rawValue) {
  const parsedValue = Number.parseInt(String(rawValue), 10);
  if (Number.isFinite(parsedValue) !== true) {
    return '';
  }
  if (parsedValue <= 0) {
    return '';
  }
  return parsedValue;
}

function isNodeInPortVisible(nodeId) {
  if (nodeId === undefined || nodeId === null) {
    return false;
  }
  if (firstCanvasNodeId.value === null) {
    return true;
  }
  return nodeId !== firstCanvasNodeId.value;
}

function isTriggerCanvasNode(node) {
  if (node === undefined || node === null || node.data === undefined || node.data === null) {
    return false;
  }
  return String(node.data.nodeType !== undefined && node.data.nodeType !== null ? node.data.nodeType : '') === 'Trigger';
}

function getActionDefaultConfig(actionType) {
  if (actionType === 'activateTool') {
    return { toolId: '', paramsMapping: {}, outputVariable: '' };
  }
  if (actionType === 'httpRequest') {
    return { method: 'POST', url: '', headersList: [], body: '{}', outputVariable: '' };
  }
  if (actionType === 'sendEmail') {
    return { provider: 'microsoft365', to: '', subject: '', body: '', outputVariable: '' };
  }
  if (actionType === 'sendSms') {
    return { provider: 'webhook', to: '', message: '', webhookUrl: '', outputVariable: '' };
  }
  if (actionType === 'queryLlm') {
    return { provider: 'webhook', prompt: '', webhookUrl: '', responsePath: '', outputVariable: '' };
  }
  if (actionType === 'executeJs') {
    return { script: '', paramsMapping: {}, outputVariable: '', useVariables: true };
  }
  if (actionType === 'sendConversation') {
    return { address: '', conversationVariable: '' };
  }
  if (actionType === 'manageVariable') {
    return { operation: 'new', variableName: '', valueType: 'string', value: '', amount: 1, fromVariable: '', outputVariable: '' };
  }
  return {};
}

function updateNodeDataById(nodeId, patchData) {
  if (nodeId === undefined || nodeId === null) {
    return;
  }
  elements.value = elements.value.map(el => {
    if (el.id !== nodeId) {
      return el;
    }
    const currentData = el.data !== undefined && el.data !== null ? el.data : {};
    const nextData = { ...currentData, ...patchData };
    const nextLabel = nextData.label !== undefined && nextData.label !== null ? String(nextData.label) : el.label;
    return {
      ...el,
      label: nextLabel,
      data: nextData
    };
  });
}

function updateNodeConfigById(nodeId, patchConfig) {
  if (nodeId === undefined || nodeId === null) {
    return;
  }
  const currentNode = elements.value.find(el => el.id === nodeId);
  if (currentNode === undefined || currentNode === null) {
    return;
  }
  const currentData = currentNode.data !== undefined && currentNode.data !== null ? currentNode.data : {};
  const currentConfig = currentData.config !== undefined && currentData.config !== null ? currentData.config : {};
  updateNodeDataById(nodeId, { config: { ...currentConfig, ...patchConfig } });
}

function updateNodeTriggerType(nodeId, triggerType) {
  if (triggerType === 'onTimer') {
    updateNodeDataById(nodeId, {
      triggerType,
      config: { mode: 'interval', delayValue: 30, delayUnit: 'seconds', intervalMinutes: 5 }
    });
    return;
  }
  updateNodeDataById(nodeId, { triggerType });
}

function addHeader(nodeId) {
  const node = elements.value.find(el => el.id === nodeId);
  if (node === undefined || node === null) return;
  const currentConfig = node.data?.config !== undefined && node.data?.config !== null ? node.data.config : {};
  const headersList = Array.isArray(currentConfig.headersList) === true ? [...currentConfig.headersList] : [];
  headersList.push({ key: '', value: '' });
  updateNodeConfigById(nodeId, { headersList });
}

function removeHeader(nodeId, index) {
  const node = elements.value.find(el => el.id === nodeId);
  if (node === undefined || node === null) return;
  const currentConfig = node.data?.config !== undefined && node.data?.config !== null ? node.data.config : {};
  const headersList = Array.isArray(currentConfig.headersList) === true ? [...currentConfig.headersList] : [];
  headersList.splice(index, 1);
  updateNodeConfigById(nodeId, { headersList });
}

function updateHeaderKey(nodeId, index, newKey) {
  const node = elements.value.find(el => el.id === nodeId);
  if (node === undefined || node === null) return;
  const currentConfig = node.data?.config !== undefined && node.data?.config !== null ? node.data.config : {};
  const headersList = Array.isArray(currentConfig.headersList) === true ? [...currentConfig.headersList] : [];
  if (headersList[index]) {
    headersList[index].key = newKey;
    updateNodeConfigById(nodeId, { headersList });
  }
}

function updateHeaderValue(nodeId, index, newValue) {
  const node = elements.value.find(el => el.id === nodeId);
  if (node === undefined || node === null) return;
  const currentConfig = node.data?.config !== undefined && node.data?.config !== null ? node.data.config : {};
  const headersList = Array.isArray(currentConfig.headersList) ? [...currentConfig.headersList] : [];
  if (headersList[index] !== undefined) {
    headersList[index].value = newValue;
    updateNodeConfigById(nodeId, { headersList });
  }
}

function updateNodeActionType(nodeId, actionType) {
  const defaultConfig = getActionDefaultConfig(actionType);
  updateNodeDataById(nodeId, { actionType, config: defaultConfig });
}

function updateNodeActivateToolById(nodeId, rawValue) {
  const parsedToolId = parsePositiveIntValue(rawValue);
  updateNodeConfigById(nodeId, { toolId: parsedToolId });
  if (parsedToolId === '') {
    return;
  }
  const toolOption = availableToolsSelectOptions.value.find(item => item.id === parsedToolId);
  if (toolOption === undefined || toolOption === null) {
    return;
  }
  updateNodeDataById(nodeId, { label: toolOption.label });
}

function updateSelectedNodeData(patchData) {
  if (selectedCanvasNode.value === null) {
    return;
  }
  updateNodeDataById(selectedCanvasNode.value.id, patchData);
}

function updateSelectedNodeConfig(patchConfig) {
  if (selectedCanvasNode.value === null) {
    return;
  }
  updateNodeConfigById(selectedCanvasNode.value.id, patchConfig);
}

function getNodeSelectedAgentIds(nodeData) {
  const config = nodeData !== undefined && nodeData !== null && nodeData.config !== undefined && nodeData.config !== null ? nodeData.config : {};
  if (Array.isArray(config.agentIds) === true) {
    return config.agentIds.map(item => String(item)).filter(item => item.length > 0);
  }
  if (config.agentId !== undefined && config.agentId !== null && String(config.agentId).length > 0) {
    return [String(config.agentId)];
  }
  return [];
}

function normalizeSelectedAgentIds(rawSelectedIds) {
  const source = Array.isArray(rawSelectedIds) === true ? rawSelectedIds : [];
  const selectedIds = [];
  for (let i = 0; i < source.length; i = i + 1) {
    const value = String(source[i] !== undefined && source[i] !== null ? source[i] : '').trim();
    if (value.length > 0) {
      selectedIds.push(value);
    }
  }
  return selectedIds;
}

function updateNodeAgentIdsByModel(nodeId, rawSelectedIds) {
  const selectedIds = normalizeSelectedAgentIds(rawSelectedIds);
  const primaryAgentId = selectedIds.length > 0 ? selectedIds[0] : '';
  updateNodeConfigById(nodeId, { agentIds: selectedIds, agentId: primaryAgentId });
}

function updateSelectedNodeAgentIdsByModel(rawSelectedIds) {
  if (selectedCanvasNode.value === null) {
    return;
  }
  const selectedIds = normalizeSelectedAgentIds(rawSelectedIds);
  const primaryAgentId = selectedIds.length > 0 ? selectedIds[0] : '';
  updateSelectedNodeConfig({ agentIds: selectedIds, agentId: primaryAgentId });
}

function getNodeSelectedConversationVariableIds(nodeData) {
  const config = nodeData !== undefined && nodeData !== null && nodeData.config !== undefined && nodeData.config !== null ? nodeData.config : {};
  const selected = String(config.conversationVariable !== undefined && config.conversationVariable !== null ? config.conversationVariable : '').trim();
  if (selected.length === 0) {
    return [];
  }
  return [selected];
}

function updateNodeConversationVariableByModel(nodeId, rawSelectedIds) {
  const selectedIds = normalizeSelectedAgentIds(rawSelectedIds);
  const selectedValue = selectedIds.length > 0 ? selectedIds[0] : '';
  updateNodeConfigById(nodeId, { conversationVariable: selectedValue });
}

function updateSelectedNodeConversationVariableByModel(rawSelectedIds) {
  if (selectedCanvasNode.value === null) {
    return;
  }
  const selectedIds = normalizeSelectedAgentIds(rawSelectedIds);
  const selectedValue = selectedIds.length > 0 ? selectedIds[0] : '';
  updateSelectedNodeConfig({ conversationVariable: selectedValue });
}

function getEdgeLabelPosition(props) {
  try {
    const [path, labelX, labelY] = getSmoothStepPath({
      sourceX: props.sourceX,
      sourceY: props.sourceY,
      targetX: props.targetX,
      targetY: props.targetY,
      sourcePosition: props.sourcePosition,
      targetPosition: props.targetPosition,
    });
    return `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`;
  } catch (error) {
    return `translate(-50%, -50%) translate(${(props.sourceX + props.targetX) / 2}px,${(props.sourceY + props.targetY) / 2}px)`;
  }
}

function handleSelectActivateTool(rawValue) {
  if (selectedCanvasNode.value === null) {
    return;
  }
  updateNodeActivateToolById(selectedCanvasNode.value.id, rawValue);
}

function getNextId() {
  return `step-${id++}`;
}

function addSupportedNode(nodeDef, dropPosition = null) {
  if (nodeDef === undefined || nodeDef === null) {
    return;
  }
  const existingNodes = elements.value.filter(el => el.position !== undefined && el.position !== null);
  if (existingNodes.length === 0) {
    if (nodeDef.data === undefined || nodeDef.data === null || nodeDef.data.nodeType !== 'Trigger') {
      return;
    }
  }
  const newNodeId = getNextId();
  const position = dropPosition !== null ? dropPosition : { x: Math.random() * 400 + 100, y: Math.random() * 200 + 50 };
  const data = nodeDef.data !== undefined && nodeDef.data !== null ? JSON.parse(JSON.stringify(nodeDef.data)) : { label: 'New Node', nodeType: 'Action' };
  const label = data.label !== undefined && data.label !== null && String(data.label).trim().length > 0 ? String(data.label) : 'New Node';
  const newNode = {
    id: newNodeId,
    type: nodeDef.type !== undefined && nodeDef.type !== null ? nodeDef.type : 'default',
    position,
    label,
    data,
    draggable: true,
  };
  addNodes([newNode]);
  selectedCanvasNodeId.value = newNodeId;
}

function addNode(type = 'Question') {
  const typeString = type !== undefined && type !== null ? String(type).trim().toLowerCase() : '';
  if (typeString === 'trigger') {
    addSupportedNode(supportedWorkflowNodeGroups[0].nodes[0]);
    return;
  }
  if (typeString === 'if') {
    addSupportedNode(supportedWorkflowNodeGroups[1].nodes[0]);
    return;
  }
  if (typeString === 'action') {
    addSupportedNode(supportedWorkflowNodeGroups[2].nodes[0]);
  }
}

function getWorkflowAssistantAuthHeaders() {
  const authToken = document.cookie.split('; ').find(row => row.startsWith('authToken=') === true)?.split('=')[1];
  const jwtToken = document.cookie.split('; ').find(row => row.startsWith('jwtToken=') === true)?.split('=')[1];
  const token = authToken !== undefined && authToken !== null && authToken !== '' ? authToken : jwtToken;
  const headers = {
    'Content-Type': 'application/json'
  };
  if (token !== undefined && token !== null && token !== '') {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

function getWorkflowAssistantTextDirection(text) {
  const value = text !== undefined && text !== null ? String(text) : '';
  const rtlRegex = /[\u0590-\u05FF\u0600-\u06FF]/;
  if (rtlRegex.test(value) === true) {
    return 'rtl';
  }
  return 'ltr';
}

function scrollWorkflowAssistantToBottom() {
  if (workflowChatMessagesRef.value !== undefined && workflowChatMessagesRef.value !== null) {
    workflowChatMessagesRef.value.scrollTop = workflowChatMessagesRef.value.scrollHeight;
  }
}

function addWorkflowAssistantMessage(type, text, isLoading = false, rawText = null) {
  const normalizedType = type === 'assistant' ? 'assistant' : 'user';
  const raw = rawText !== undefined && rawText !== null ? String(rawText) : String(text !== undefined && text !== null ? text : '');
  const rendered = normalizedType === 'assistant' && isLoading === false ? sanitizeHtml(marked.parse(String(text !== undefined && text !== null ? text : ''))) : String(text !== undefined && text !== null ? text : '');
  const messageId = workflowAssistantMessageId;
  workflowAssistantMessageId = workflowAssistantMessageId + 1;
  workflowAssistantMessages.value.push({
    id: messageId,
    type: normalizedType,
    text: rendered,
    rawText: raw,
    isLoading: isLoading === true,
    direction: getWorkflowAssistantTextDirection(raw)
  });
  nextTick(() => {
    scrollWorkflowAssistantToBottom();
  });
  return messageId;
}

function updateWorkflowAssistantMessageById(messageId, answerText) {
  const index = workflowAssistantMessages.value.findIndex(message => message.id === messageId);
  if (index < 0) {
    return;
  }
  const raw = answerText !== undefined && answerText !== null ? String(answerText) : '';
  workflowAssistantMessages.value[index] = {
    ...workflowAssistantMessages.value[index],
    type: 'assistant',
    text: sanitizeHtml(marked.parse(raw)),
    rawText: raw,
    isLoading: false,
    direction: getWorkflowAssistantTextDirection(raw)
  };
  nextTick(() => {
    scrollWorkflowAssistantToBottom();
  });
}

async function initializeWorkflowAssistantSession() {
  if (workflowAssistantSessionId.value !== null && workflowAssistantSessionId.value !== undefined && String(workflowAssistantSessionId.value).trim().length > 0) {
    return;
  }
  workflowAssistantIsLoading.value = true;
  try {
    const headers = getWorkflowAssistantAuthHeaders();
    const [infoResponse, sessionResponse] = await Promise.all([
      axios.get(`${apiEndpoint.value}/api/helpAgent/info`, { headers }),
      axios.post(`${apiEndpoint.value}/api/helpAgent/createSession`, {}, { headers })
    ]);
    const sessionIdRaw = sessionResponse !== undefined && sessionResponse !== null ? sessionResponse.data?.sessionId : '';
    if (sessionIdRaw === undefined || sessionIdRaw === null || String(sessionIdRaw).trim().length === 0) {
      throw new Error('missing workflow assistant session id');
    }
    workflowAssistantSessionId.value = String(sessionIdRaw);
    if (workflowAssistantMessages.value.length === 0) {
      const startingMessage = 'Workflow assistant is ready. Describe your process in plain language and I will add nodes, connect them, and fill the fields for you.';
      addWorkflowAssistantMessage('assistant', startingMessage, false, startingMessage);
    }
  } catch (error) {
    if (workflowAssistantMessages.value.length === 0) {
      addWorkflowAssistantMessage('assistant', 'Tell me what you want to automate, and I will build it and fill the fields for you.', false, 'Tell me what you want to automate, and I will build it and fill the fields for you.');
    }
  } finally {
    workflowAssistantIsLoading.value = false;
  }
}

function showWorkflowNodesPanel() {
  isWorkflowChatOpen.value = false;
}

async function showWorkflowChatPanel() {
  isWorkflowChatOpen.value = true;
  await initializeWorkflowAssistantSession();
  await nextTick();
  scrollWorkflowAssistantToBottom();
}

function getSelectedNodeData() {
  if (selectedCanvasNode.value === null) {
    return null;
  }
  const node = selectedCanvasNode.value;
  if (node.data === undefined || node.data === null) {
    return null;
  }
  return node;
}

function tryAddNodeFromPrompt(normalizedPrompt) {
  if (normalizedPrompt.includes('add') !== true) {
    return false;
  }
  if (normalizedPrompt.includes('trigger') === true && normalizedPrompt.includes('webhook') === true) {
    addSupportedNode(supportedWorkflowNodeGroups[0].nodes[2]);
    addWorkflowAssistantMessage('assistant', 'Added Trigger: On Webhook.');
    return true;
  }
  if (normalizedPrompt.includes('trigger') === true && normalizedPrompt.includes('analysis') === true) {
    addSupportedNode(supportedWorkflowNodeGroups[0].nodes[1]);
    addWorkflowAssistantMessage('assistant', 'Added Trigger: On Call Analysis.');
    return true;
  }
  if (normalizedPrompt.includes('trigger') === true && normalizedPrompt.includes('timer') === true) {
    addSupportedNode(supportedWorkflowNodeGroups[0].nodes[3]);
    addWorkflowAssistantMessage('assistant', 'Added Trigger: On Timer.');
    return true;
  }
  if (normalizedPrompt.includes('trigger') === true && normalizedPrompt.includes('call end') === true) {
    addSupportedNode(supportedWorkflowNodeGroups[0].nodes[0]);
    addWorkflowAssistantMessage('assistant', 'Added Trigger: On Call End.');
    return true;
  }
  if (normalizedPrompt.includes('if') === true) {
    addSupportedNode(supportedWorkflowNodeGroups[1].nodes[0]);
    addWorkflowAssistantMessage('assistant', 'Added Logic: If Condition.');
    return true;
  }
  if (normalizedPrompt.includes('action') === true && normalizedPrompt.includes('http') === true) {
    addSupportedNode(supportedWorkflowNodeGroups[2].nodes[1]);
    addWorkflowAssistantMessage('assistant', 'Added Action: HTTP Request.');
    return true;
  }
  if (normalizedPrompt.includes('action') === true && normalizedPrompt.includes('email') === true) {
    addSupportedNode(supportedWorkflowNodeGroups[2].nodes[2]);
    addWorkflowAssistantMessage('assistant', 'Added Action: Send Email.');
    return true;
  }
  if (normalizedPrompt.includes('action') === true && normalizedPrompt.includes('js') === true) {
    addSupportedNode(supportedWorkflowNodeGroups[2].nodes[3]);
    addWorkflowAssistantMessage('assistant', 'Added Action: Execute JS.');
    return true;
  }
  if (normalizedPrompt.includes('variable') === true) {
    addSupportedNode(supportedWorkflowNodeGroups[2].nodes[5]);
    addWorkflowAssistantMessage('assistant', 'Added Action: Manage Variable.');
    return true;
  }
  if (normalizedPrompt.includes('action') === true && normalizedPrompt.includes('tool') === true) {
    addSupportedNode(supportedWorkflowNodeGroups[2].nodes[0]);
    addWorkflowAssistantMessage('assistant', 'Added Action: Activate Tool.');
    return true;
  }
  if (normalizedPrompt.includes('add action') === true) {
    addSupportedNode(supportedWorkflowNodeGroups[2].nodes[0]);
    addWorkflowAssistantMessage('assistant', 'Added Action node.');
    return true;
  }
  return false;
}

function tryModifyNodeFromPrompt(rawPrompt, normalizedPrompt) {
  if (normalizedPrompt.includes('organize') === true || normalizedPrompt.includes('arrange') === true) {
    organizeFlow();
    addWorkflowAssistantMessage('assistant', 'Reorganized workflow from left to right.');
    return true;
  }
  if (normalizedPrompt.includes('set operator') === true) {
    const selected = getSelectedNodeData();
    if (selected === null || selected.data.nodeType !== 'If') {
      addWorkflowAssistantMessage('assistant', 'Select an If node first, then ask me to set operator.');
      return true;
    }
    const operatorTokens = ['equals', 'notequals', 'contains', 'greaterthan', 'lessthan', 'exists', 'isempty'];
    let chosenOperator = '';
    for (let i = 0; i < operatorTokens.length; i = i + 1) {
      if (normalizedPrompt.includes(operatorTokens[i]) === true) {
        chosenOperator = operatorTokens[i];
      }
    }
    if (chosenOperator.length === 0) {
      addWorkflowAssistantMessage('assistant', 'Use one of: equals, notEquals, contains, greaterThan, lessThan, exists, isEmpty.');
      return true;
    }
    if (chosenOperator === 'notequals') {
      chosenOperator = 'notEquals';
    }
    if (chosenOperator === 'greaterthan') {
      chosenOperator = 'greaterThan';
    }
    if (chosenOperator === 'lessthan') {
      chosenOperator = 'lessThan';
    }
    if (chosenOperator === 'isempty') {
      chosenOperator = 'isEmpty';
    }
    updateNodeConfigById(selected.id, { operator: chosenOperator });
    addWorkflowAssistantMessage('assistant', `Updated If operator to ${chosenOperator}.`);
    return true;
  }
  if (normalizedPrompt.includes('set trigger') === true) {
    const selected = getSelectedNodeData();
    if (selected === null || selected.data.nodeType !== 'Trigger') {
      addWorkflowAssistantMessage('assistant', 'Select a Trigger node first, then ask me to set trigger.');
      return true;
    }
    let triggerType = '';
    if (normalizedPrompt.includes('webhook') === true) {
      triggerType = 'onWebhook';
    }
    if (normalizedPrompt.includes('analysis') === true) {
      triggerType = 'onCallAnalysis';
    }
    if (normalizedPrompt.includes('call end') === true) {
      triggerType = 'onCallEnd';
    }
    if (normalizedPrompt.includes('timer') === true) {
      triggerType = 'onTimer';
    }
    if (triggerType.length === 0) {
      addWorkflowAssistantMessage('assistant', 'Use one of: onCallEnd, onCallAnalysis, onWebhook, onTimer.');
      return true;
    }
    updateNodeTriggerType(selected.id, triggerType);
    addWorkflowAssistantMessage('assistant', `Updated Trigger to ${triggerType}.`);
    return true;
  }
  if (normalizedPrompt.includes('rename selected to ') === true) {
    const selected = getSelectedNodeData();
    if (selected === null) {
      addWorkflowAssistantMessage('assistant', 'Select a node first, then ask me to rename it.');
      return true;
    }
    const marker = 'rename selected to ';
    const markerIndex = normalizedPrompt.indexOf(marker);
    if (markerIndex < 0) {
      return false;
    }
    const newLabel = rawPrompt.slice(markerIndex + marker.length).trim();
    if (newLabel.length === 0) {
      addWorkflowAssistantMessage('assistant', 'Please provide a name after "rename selected to".');
      return true;
    }
    updateNodeDataById(selected.id, { label: newLabel });
    addWorkflowAssistantMessage('assistant', `Renamed selected node to "${newLabel}".`);
    return true;
  }
  return false;
}

function extractFirstUrl(text) {
  const value = String(text !== undefined && text !== null ? text : '');
  const match = value.match(/https?:\/\/[^\s"')]+/i);
  if (match === null || match[0] === undefined) {
    return '';
  }
  return String(match[0]).trim();
}

function extractFirstEmail(text) {
  const value = String(text !== undefined && text !== null ? text : '');
  const match = value.match(/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/);
  if (match === null || match[0] === undefined) {
    return '';
  }
  return String(match[0]).trim();
}

function detectHttpMethodFromPrompt(normalizedPrompt) {
  if (normalizedPrompt.includes('method post') === true || normalizedPrompt.includes(' use post') === true || normalizedPrompt.includes(' post ') === true) {
    return 'POST';
  }
  if (normalizedPrompt.includes('method put') === true || normalizedPrompt.includes(' use put') === true || normalizedPrompt.includes(' put ') === true || normalizedPrompt.includes(' update ') === true) {
    return 'PUT';
  }
  if (normalizedPrompt.includes('method patch') === true || normalizedPrompt.includes(' use patch') === true || normalizedPrompt.includes(' patch ') === true) {
    return 'PATCH';
  }
  if (normalizedPrompt.includes('method delete') === true || normalizedPrompt.includes(' use delete') === true || normalizedPrompt.includes(' delete ') === true) {
    return 'DELETE';
  }
  if (normalizedPrompt.includes('method get') === true || normalizedPrompt.includes(' use get') === true || normalizedPrompt.includes(' get ') === true) {
    return 'GET';
  }
  return '';
}

function tryPopulateNodeFieldsFromPrompt(rawPrompt, normalizedPrompt) {
  const selected = getSelectedNodeData();
  if (selected === null) {
    return false;
  }
  const nodeType = String(selected.data?.nodeType !== undefined && selected.data?.nodeType !== null ? selected.data.nodeType : '');
  const actionType = String(selected.data?.actionType !== undefined && selected.data?.actionType !== null ? selected.data.actionType : '');
  if (nodeType === 'Trigger') {
    if (normalizedPrompt.includes('call end') === true || normalizedPrompt.includes('conversation close') === true || normalizedPrompt.includes('close conversation') === true) {
      updateNodeTriggerType(selected.id, 'onCallEnd');
      addWorkflowAssistantMessage('assistant', 'Updated selected trigger to onCallEnd.');
      return true;
    }
    if (normalizedPrompt.includes('analysis') === true) {
      updateNodeTriggerType(selected.id, 'onCallAnalysis');
      addWorkflowAssistantMessage('assistant', 'Updated selected trigger to onCallAnalysis.');
      return true;
    }
    if (normalizedPrompt.includes('webhook') === true) {
      updateNodeTriggerType(selected.id, 'onWebhook');
      addWorkflowAssistantMessage('assistant', 'Updated selected trigger to onWebhook.');
      return true;
    }
    if (normalizedPrompt.includes('timer') === true) {
      updateNodeTriggerType(selected.id, 'onTimer');
      addWorkflowAssistantMessage('assistant', 'Updated selected trigger to onTimer.');
      return true;
    }
  }
  if (nodeType === 'Action' && actionType === 'sendConversation') {
    const url = extractFirstUrl(rawPrompt);
    if (url.length > 0) {
      updateNodeConfigById(selected.id, { address: url });
      addWorkflowAssistantMessage('assistant', `Updated send conversation webhook URL to ${url}.`);
      return true;
    }
  }
  if (nodeType === 'Action' && actionType === 'httpRequest') {
    const patchConfig = {};
    const url = extractFirstUrl(rawPrompt);
    if (url.length > 0) {
      patchConfig.url = url;
    }
    const method = detectHttpMethodFromPrompt(normalizedPrompt);
    if (method.length > 0) {
      patchConfig.method = method;
    }
    const keys = Object.keys(patchConfig);
    if (keys.length > 0) {
      updateNodeConfigById(selected.id, patchConfig);
      if (patchConfig.url !== undefined && patchConfig.method !== undefined) {
        addWorkflowAssistantMessage('assistant', `Updated HTTP Request URL to ${patchConfig.url} and method to ${patchConfig.method}.`);
      } else if (patchConfig.url !== undefined) {
        addWorkflowAssistantMessage('assistant', `Updated HTTP Request URL to ${patchConfig.url}.`);
      } else {
        addWorkflowAssistantMessage('assistant', `Updated HTTP Request method to ${patchConfig.method}.`);
      }
      return true;
    }
  }
  if (nodeType === 'Action' && actionType === 'sendEmail') {
    const email = extractFirstEmail(rawPrompt);
    if (email.length > 0) {
      updateNodeConfigById(selected.id, { to: email });
      addWorkflowAssistantMessage('assistant', `Updated email recipient to ${email}.`);
      return true;
    }
  }
  return false;
}

function buildWorkflowAssistantLibrarySummary() {
  const groups = [];
  for (let i = 0; i < allSupportedNodeGroups.value.length; i = i + 1) {
    const group = allSupportedNodeGroups.value[i];
    if (group === undefined || group === null) {
      continue;
    }
    const nodes = Array.isArray(group.nodes) === true ? group.nodes : [];
    const compactNodes = [];
    for (let j = 0; j < nodes.length; j = j + 1) {
      const node = nodes[j];
      if (node === undefined || node === null) {
        continue;
      }
      compactNodes.push({
        id: node.id,
        label: node.label,
        description: node.description,
        nodeType: node.data !== undefined && node.data !== null ? node.data.nodeType : '',
        triggerType: node.data !== undefined && node.data !== null ? node.data.triggerType : '',
        actionType: node.data !== undefined && node.data !== null ? node.data.actionType : ''
      });
    }
    groups.push({
      id: group.id,
      label: group.label,
      nodes: compactNodes
    });
  }
  return groups;
}

function buildWorkflowAssistantQuestion(rawPrompt) {
  const flow = getFlowData();
  const contextPayload = {
    selectedNodeId: selectedCanvasNodeId.value,
    workflow: flow,
    supportedNodeLibrary: buildWorkflowAssistantLibrarySummary(),
    availableVariables: workflowVariableOptions.value
  };
  const contextJson = JSON.stringify(contextPayload);
  return `User request:\n${rawPrompt}\n\nWorkflow context JSON:\n${contextJson}\n\nThe user is non-technical. Convert plain-language requests into concrete workflow edits.\nAlways fill real node fields when the user gives values such as URL, method, email, variable, title, condition, or trigger.\nIf possible, return actions in <workflow_actions>JSON</workflow_actions> so the editor can apply changes automatically.\nSupported action types: addNode, updateNode, connectNodes, organizeFlow.\nFor updateNode use patchData and patchConfig. Prefer target: "selected" when a node is selected.\nAction format examples:\n[{\"type\":\"addNode\",\"nodeDefId\":\"action-http-request\"},{\"type\":\"updateNode\",\"target\":\"selected\",\"patchConfig\":{\"method\":\"POST\",\"url\":\"https://example.com/webhook\"}},{\"type\":\"connectNodes\",\"source\":\"selected\",\"target\":\"step-3\",\"sourceHandle\":\"success\"}]\nNode field mapping:\n- Trigger: triggerType (onCallEnd,onCallAnalysis,onWebhook,onTimer)\n- HTTP Request: config.method, config.url, config.body, config.headersList\n- Send Email: config.to, config.subject, config.body\n- Send Conversation: config.address, config.conversationVariable\n- Manage Variable: config.operation, config.variableName, config.valueType, config.value, config.amount, config.fromVariable\nUse nodeDefId from supportedNodeLibrary ids and variables from availableVariables.`;
}

function sendWorkflowAssistantQuickPrompt(value) {
  const prompt = String(value !== undefined && value !== null ? value : '').trim();
  if (prompt.length === 0) {
    return;
  }
  workflowAssistantInput.value = prompt;
  submitWorkflowAssistantPrompt();
}

function normalizeWorkflowAssistantAnswer(answerText, appliedChangesCount) {
  const raw = String(answerText !== undefined && answerText !== null ? answerText : '').trim();
  const lower = raw.toLowerCase();
  if (appliedChangesCount > 0 && (lower.includes('sonarix support assistant') === true || lower.includes('navigate the application') === true)) {
    return `Done. I applied ${appliedChangesCount} workflow change${appliedChangesCount === 1 ? '' : 's'}.`;
  }
  if (lower.includes('sonarix support assistant') === true || lower.includes('navigate the application') === true) {
    return 'Describe the workflow outcome you want, and I will create the nodes and set the fields for you.';
  }
  return raw;
}

function extractWorkflowActionsFromAnswer(answerText) {
  const text = typeof answerText === 'string' ? answerText : '';
  const match = text.match(/<workflow_actions>([\s\S]*?)<\/workflow_actions>/i);
  if (match === null || match[1] === undefined) {
    return [];
  }
  const payload = String(match[1]).trim();
  if (payload.length === 0) {
    return [];
  }
  try {
    const parsed = JSON.parse(payload);
    if (Array.isArray(parsed) === true) {
      return parsed;
    }
    if (parsed !== undefined && parsed !== null && Array.isArray(parsed.actions) === true) {
      return parsed.actions;
    }
    return [];
  } catch (error) {
    return [];
  }
}

function stripWorkflowActionsTag(answerText) {
  const text = typeof answerText === 'string' ? answerText : '';
  return text.replace(/<workflow_actions>[\s\S]*?<\/workflow_actions>/gi, '').trim();
}

function findNodeByLabelRef(labelRef) {
  const label = String(labelRef !== undefined && labelRef !== null ? labelRef : '').trim().toLowerCase();
  if (label.length === 0) {
    return null;
  }
  const nodes = elements.value.filter(el => el.position !== undefined && el.position !== null);
  for (let i = 0; i < nodes.length; i = i + 1) {
    const node = nodes[i];
    const nodeLabel = String(node.data !== undefined && node.data !== null && node.data.label !== undefined ? node.data.label : node.label !== undefined ? node.label : '').trim().toLowerCase();
    if (nodeLabel === label) {
      return node;
    }
  }
  return null;
}

function resolveNodeIdFromRef(refValue, selectedId, lastAddedNodeId) {
  const ref = String(refValue !== undefined && refValue !== null ? refValue : '').trim();
  if (ref.length === 0) {
    return null;
  }
  const normalized = ref.toLowerCase();
  if (normalized === 'selected') {
    return selectedId;
  }
  if (normalized === 'lastadded' || normalized === 'last_added') {
    return lastAddedNodeId;
  }
  const byId = elements.value.find(el => el.position !== undefined && el.position !== null && el.id === ref);
  if (byId !== undefined && byId !== null) {
    return byId.id;
  }
  const byLabel = findNodeByLabelRef(ref);
  if (byLabel !== null) {
    return byLabel.id;
  }
  return null;
}

function addWorkflowEdgeFromRefs(sourceId, targetId, sourceHandle = '', targetHandle = '') {
  if (sourceId === null || sourceId === undefined || targetId === null || targetId === undefined) {
    return false;
  }
  const sourceNode = findNode(sourceId);
  const targetNode = findNode(targetId);
  if (sourceNode === undefined || sourceNode === null || targetNode === undefined || targetNode === null) {
    return false;
  }
  if (firstCanvasNodeId.value !== null && targetNode.id === firstCanvasNodeId.value) {
    return false;
  }
  if (isTriggerCanvasNode(targetNode) === true) {
    toast.error('Trigger nodes cannot have incoming edges.');
    return false;
  }
  const edgeHandle = String(sourceHandle !== undefined && sourceHandle !== null ? sourceHandle : '').trim();
  const nextTargetHandle = String(targetHandle !== undefined && targetHandle !== null ? targetHandle : '').trim();
  const newEdge = {
    id: `e-${sourceId}-${targetId}-${new Date().getTime()}`,
    source: sourceId,
    target: targetId,
    sourceHandle: edgeHandle.length > 0 ? edgeHandle : undefined,
    targetHandle: nextTargetHandle.length > 0 ? nextTargetHandle : undefined,
    type: 'smoothstep',
    markerEnd: { type: MarkerType.ArrowClosed, width: 20, height: 20, color: '#7c3aed' },
    style: { stroke: '#7c3aed', strokeWidth: 2 },
    data: {
      ifCondition: '',
      branch: edgeHandle
    },
    label: ''
  };
  if ((sourceNode.data !== undefined && sourceNode.data !== null && sourceNode.data.nodeType === 'If') || (sourceNode.data !== undefined && sourceNode.data !== null && sourceNode.data.nodeType === 'Action')) {
    if (edgeHandle.length > 0) {
      newEdge.label = edgeHandle;
    }
  }
  addEdges([newEdge]);
  return true;
}

function applyWorkflowAssistantActions(actions) {
  if (Array.isArray(actions) !== true || actions.length === 0) {
    return 0;
  }
  let appliedCount = 0;
  let lastAddedNodeId = null;
  const initiallySelectedId = selectedCanvasNodeId.value;
  for (let i = 0; i < actions.length; i = i + 1) {
    const action = actions[i];
    if (action === undefined || action === null || typeof action !== 'object') {
      continue;
    }
    const actionType = String(action.type !== undefined && action.type !== null ? action.type : '').trim().toLowerCase();
    if (actionType === 'addnode' || actionType === 'add_node') {
      const nodeDefId = String(action.nodeDefId !== undefined && action.nodeDefId !== null ? action.nodeDefId : action.libraryId !== undefined && action.libraryId !== null ? action.libraryId : '').trim();
      if (nodeDefId.length === 0) {
        continue;
      }
      const nodeDef = getWorkflowNodeById(nodeDefId);
      if (nodeDef === undefined || nodeDef === null) {
        continue;
      }
      addSupportedNode(nodeDef);
      if (selectedCanvasNodeId.value !== null) {
        lastAddedNodeId = selectedCanvasNodeId.value;
      }
      appliedCount = appliedCount + 1;
      continue;
    }
    if (actionType === 'updatenode' || actionType === 'update_node') {
      const targetRef = action.target !== undefined ? action.target : action.targetRef;
      const targetId = resolveNodeIdFromRef(targetRef, selectedCanvasNodeId.value, lastAddedNodeId);
      if (targetId === null || targetId === undefined) {
        continue;
      }
      if (action.patchData !== undefined && action.patchData !== null && typeof action.patchData === 'object') {
        updateNodeDataById(targetId, action.patchData);
      }
      if (action.patchConfig !== undefined && action.patchConfig !== null && typeof action.patchConfig === 'object') {
        updateNodeConfigById(targetId, action.patchConfig);
      }
      appliedCount = appliedCount + 1;
      continue;
    }
    if (actionType === 'connectnodes' || actionType === 'connect_nodes') {
      const sourceRef = action.source !== undefined ? action.source : action.sourceRef;
      const targetRef = action.target !== undefined ? action.target : action.targetRef;
      const sourceId = resolveNodeIdFromRef(sourceRef, initiallySelectedId, lastAddedNodeId);
      const targetId = resolveNodeIdFromRef(targetRef, selectedCanvasNodeId.value, lastAddedNodeId);
      const sourceHandle = action.sourceHandle !== undefined ? action.sourceHandle : '';
      const targetHandle = action.targetHandle !== undefined ? action.targetHandle : '';
      if (addWorkflowEdgeFromRefs(sourceId, targetId, sourceHandle, targetHandle) === true) {
        appliedCount = appliedCount + 1;
      }
      continue;
    }
    if (actionType === 'organizeflow' || actionType === 'organize_flow') {
      organizeFlow();
      appliedCount = appliedCount + 1;
    }
  }
  return appliedCount;
}

async function submitWorkflowAssistantPrompt() {
  const rawPrompt = String(workflowAssistantInput.value !== undefined && workflowAssistantInput.value !== null ? workflowAssistantInput.value : '').trim();
  if (rawPrompt.length === 0) {
    return;
  }
  addWorkflowAssistantMessage('user', rawPrompt, false, rawPrompt);
  workflowAssistantInput.value = '';
  const normalizedPrompt = rawPrompt.toLowerCase();
  if (tryAddNodeFromPrompt(normalizedPrompt) === true) {
    return;
  }
  if (tryModifyNodeFromPrompt(rawPrompt, normalizedPrompt) === true) {
    return;
  }
  if (tryPopulateNodeFieldsFromPrompt(rawPrompt, normalizedPrompt) === true) {
    return;
  }
  await initializeWorkflowAssistantSession();
  if (workflowAssistantSessionId.value === null || workflowAssistantSessionId.value === undefined || String(workflowAssistantSessionId.value).trim().length === 0) {
    addWorkflowAssistantMessage('assistant', 'I could not start the assistant service right now. Please try again.');
    return;
  }
  const loadingMessageId = addWorkflowAssistantMessage('assistant', '', true, '');
  try {
    const contextualQuestion = buildWorkflowAssistantQuestion(rawPrompt);
    const response = await axios.post(`${apiEndpoint.value}/api/helpAgent/query`, {
      sessionId: workflowAssistantSessionId.value,
      question: contextualQuestion
    }, {
      headers: getWorkflowAssistantAuthHeaders()
    });
    const answerTextRaw = response !== undefined && response !== null && typeof response.data?.answer === 'string' && response.data.answer.trim().length > 0
      ? response.data.answer
      : 'I am not sure yet. Please try rephrasing your request.';
    const workflowActions = extractWorkflowActionsFromAnswer(answerTextRaw);
    const appliedChangesCount = applyWorkflowAssistantActions(workflowActions);
    const cleanedAnswer = stripWorkflowActionsTag(answerTextRaw);
    const normalizedAnswer = normalizeWorkflowAssistantAnswer(cleanedAnswer, appliedChangesCount);
    const fallbackAnswer = appliedChangesCount > 0 ? `Applied ${appliedChangesCount} workflow change${appliedChangesCount === 1 ? '' : 's'}.` : 'I am not sure yet. Please try rephrasing your request.';
    const finalAnswer = normalizedAnswer.length > 0 ? normalizedAnswer : fallbackAnswer;
    updateWorkflowAssistantMessageById(loadingMessageId, finalAnswer);
  } catch (error) {
    updateWorkflowAssistantMessageById(loadingMessageId, 'I could not reach the assistant service right now. Please try again.');
  }
}

function onDragStart(event, nodeDefinition) {
  if (nodeDefinition === undefined || nodeDefinition === null) {
    return;
  }
  if (event.dataTransfer !== undefined && event.dataTransfer !== null) {
    event.dataTransfer.setData('application/vueflow', JSON.stringify({ nodeId: nodeDefinition.id }));
    event.dataTransfer.effectAllowed = 'move';
  }
  dragData.value = { nodeId: nodeDefinition.id };
}

function onDrop(event) {
  event.preventDefault();
  const flowContainer = event.currentTarget;
  const { left, top } = flowContainer.getBoundingClientRect();
  let nodeIdToDrop = null;
  try {
    const jsonData = event.dataTransfer.getData('application/vueflow');
    if (jsonData !== undefined && jsonData !== null && jsonData.length > 0) {
      const parsed = JSON.parse(jsonData);
      if (parsed !== undefined && parsed !== null) {
        if (parsed.nodeId !== undefined && parsed.nodeId !== null) {
          nodeIdToDrop = parsed.nodeId;
        }
      }
    }
    if (nodeIdToDrop === null) {
      if (dragData.value !== null) {
        if (dragData.value.nodeId !== undefined && dragData.value.nodeId !== null) {
          nodeIdToDrop = dragData.value.nodeId;
        }
      }
    }
  } catch (error) {
    if (dragData.value !== null) {
      if (dragData.value.nodeId !== undefined && dragData.value.nodeId !== null) {
        nodeIdToDrop = dragData.value.nodeId;
      }
    }
  }

  if (nodeIdToDrop === undefined || nodeIdToDrop === null) {
    dragData.value = null;
    return;
  }

  const position = project({
    x: event.clientX - left,
    y: event.clientY - top,
  });

  const definition = getWorkflowNodeById(nodeIdToDrop);
  if (definition === undefined || definition === null) {
    dragData.value = null;
    return;
  }

  addSupportedNode(definition, position);
  dragData.value = null;
}

onConnect((params) => {
  if (params === undefined || params === null) {
    return;
  }
  const sourceNode = findNode(params.source);
  if (sourceNode === undefined || sourceNode === null) {
    return;
  }
  const targetNode = findNode(params.target);
  if (targetNode === undefined || targetNode === null) {
    return;
  }
  if (firstCanvasNodeId.value !== null && targetNode.id === firstCanvasNodeId.value) {
    return;
  }
  if (isTriggerCanvasNode(targetNode) === true) {
    toast.error('Trigger nodes cannot have incoming edges.');
    return;
  }
  const newEdge = { 
    id: `e-${params.source}-${params.target}-${new Date().getTime()}`, 
    ...params, 
    type: 'smoothstep',
    markerEnd: { type: MarkerType.ArrowClosed, width: 20, height: 20, color: '#7c3aed' },
    style: { stroke: '#7c3aed', strokeWidth: 2 },
    data: {
      ifCondition: '',
      branch: params.sourceHandle !== undefined && params.sourceHandle !== null ? String(params.sourceHandle) : ''
    },
    label: '' 
  };
  if (sourceNode.data !== undefined && sourceNode.data !== null && sourceNode.data.nodeType === 'If') {
    if (params.sourceHandle !== undefined && params.sourceHandle !== null) {
      newEdge.label = String(params.sourceHandle);
    }
  }
  if (sourceNode.data !== undefined && sourceNode.data !== null && sourceNode.data.nodeType === 'Action') {
    if (params.sourceHandle !== undefined && params.sourceHandle !== null) {
      newEdge.label = String(params.sourceHandle);
    }
  }
  addEdges([newEdge]);
});

function handleNodeDoubleClick(event) {
  editingNodeId.value = event.node.id;
  editingNodeData.value = JSON.parse(JSON.stringify(event.node.data)); 
  
  if (editingNodeData.value.nodeType === 'Question' || editingNodeData.value.nodeType === 'Root') {
    isStepModalOpen.value = true;
  } else if (editingNodeData.value.nodeType === 'KnowledgeBase') {
    isStepModalOpen.value = true;
  }
}

function handleEdgeDoubleClick({ edge, event }) {
  if (edge === undefined || edge === null) {
    return;
  }
  event.stopPropagation(); 
  event.preventDefault();  
  editingEdgeId.value = edge.id;
  if (edge.data !== undefined && edge.data !== null && edge.data.ifCondition !== undefined && edge.data.ifCondition !== null && edge.data.ifCondition !== '') {
    currentEdgeCondition.value = edge.data.ifCondition;
  } else {
    currentEdgeCondition.value = '';
  }
  isEdgeModalOpen.value = true;
}

function closeEdgeModal() {
    isEdgeModalOpen.value = false;
    editingEdgeId.value = null;
    currentEdgeCondition.value = '';
}

function saveEdgeCondition(condition) {
    if (editingEdgeId.value !== null) {
        const edge = findEdge(editingEdgeId.value);
        if (edge !== undefined && edge !== null) {
            edge.label = condition;
            if (edge.data === undefined || edge.data === null) {
                edge.data = {}
            }
            edge.data.ifCondition = condition;
            
            elements.value = elements.value.map(el => {
                if (el.id === editingEdgeId.value && el.source !== undefined && el.target !== undefined) {
                    const newData = el.data !== undefined && el.data !== null ? { ...el.data, ifCondition: condition } : { ifCondition: condition }
                    return { ...el, label: condition, data: newData }
                }
                return el;
            });
        }
    }
    closeEdgeModal();
}

function handleDeleteEdgeFromModal() {
    if (editingEdgeId.value !== null) {
        removeEdges([editingEdgeId.value]);
    }
    closeEdgeModal();
}

function requestDeleteSelectedElements() {
  const selectedNodes = getSelectedNodes.value;
  const selectedEdges = getSelectedEdges.value;

  if (selectedNodes.length === 0 && selectedEdges.length === 0) return;

  elementsToDelete.value = { 
    nodes: selectedNodes.map(n => n.id), 
    edges: selectedEdges.map(e => e.id) 
  };
  isDeleteConfirmOpen.value = true;
}

function confirmDelete() {
  const nodeElements = elements.value.filter(el => el.position !== undefined && el.position !== null);
  const nodesAfterDelete = nodeElements.filter(node => elementsToDelete.value.nodes.includes(node.id) === false);
  if (nodesAfterDelete.length > 0) {
    const hasTriggerAfterDelete = nodesAfterDelete.some(node => node.data !== undefined && node.data !== null && node.data.nodeType === 'Trigger');
    if (hasTriggerAfterDelete !== true) {
      toast.error('Workflow must include at least one Trigger node.');
      cancelDelete();
      return;
    }
    const firstNodeAfterDelete = nodesAfterDelete[0];
    if (firstNodeAfterDelete !== undefined && firstNodeAfterDelete !== null) {
      const firstNodeType = firstNodeAfterDelete.data !== undefined && firstNodeAfterDelete.data !== null ? firstNodeAfterDelete.data.nodeType : '';
      if (firstNodeType !== 'Trigger') {
        toast.error('The first node must be a Trigger node.');
        cancelDelete();
        return;
      }
    }
  }
  if (selectedCanvasNodeId.value !== null) {
    if (elementsToDelete.value.nodes.includes(selectedCanvasNodeId.value) === true) {
      selectedCanvasNodeId.value = null;
    }
  }
  if (elementsToDelete.value.nodes.length > 0) {
    removeNodes(elementsToDelete.value.nodes);
  }
  if (elementsToDelete.value.edges.length > 0) {
    removeEdges(elementsToDelete.value.edges);
  }
  cancelDelete();
}

function cancelDelete() {
  isDeleteConfirmOpen.value = false;
  elementsToDelete.value = { nodes: [], edges: [] };
}

function handleKeyDown(event) {
  const tagName = event.target.tagName.toLowerCase();
  if (isEdgeModalOpen.value === true || isStepModalOpen.value === true || isActionStepModalOpen.value === true) {
    return;
  }
  if (tagName === 'input' || tagName === 'textarea') {
    return;
  }
  if (event.key === 'Delete' || event.key === 'Backspace') {
    requestDeleteSelectedElements();
  }
}

async function fetchAvailableTools() {
  try {
    const response = await axios.get(`${apiEndpoint.value}/api/tools/getAllAITools`);
    availableTools.value = Array.isArray(response.data) === true ? response.data : [];
  } catch (error) {
    availableTools.value = [];
  }
}

async function fetchAvailableAgents() {
  try {
    const data = await getAllAIChats({ includeStyles: false, force: true });
    const list = Array.isArray(data) === true ? data : [];
    availableAgents.value = list.map((agent) => {
      const idValue = agent?.agentid!==undefined && agent?.agentid!==null ? String(agent?.agentid) : agent?.agentId!==undefined && agent?.agentId!==null ? String(agent?.agentId) : agent?._id!==undefined && agent?._id!==null ? String(agent?._id) : agent?.id!==undefined && agent?.id!==null ? String(agent?.id) : '';
      const nameValue = agent?.agentname!==undefined && agent?.agentname!==null ? String(agent?.agentname) : agent?.agentName!==undefined && agent?.agentName!==null ? String(agent?.agentName) : agent?.title!==undefined && agent?.title!==null ? String(agent?.title) : agent?.name!==undefined && agent?.name!==null ? String(agent?.name) : '';
      return {
        ...agent,
        agentid: String(idValue),
        agentname: String(nameValue),
      };
    }).filter((agent) => agent.agentid.length > 0 && agent.agentname.length > 0);
  } catch (error) {
    console.error('Failed to load agents', error);
    availableAgents.value = [];
  }
}

async function fetchAvailableTags() {
  try {
    const response = await axios.get(`${apiEndpoint.value}/api/tags/`);
    availableTags.value = Array.isArray(response.data) === true ? response.data : [];
  } catch (error) {
    availableTags.value = [];
  }
}

function getFlowData() {
  const nodes = [];
  const edges = [];
  for (let i = 0; i < elements.value.length; i = i + 1) {
    const el = elements.value[i];
    if (el.position !== undefined && el.position !== null) {
      nodes.push({
        id: el.id,
        type: el.type,
        position: el.position,
        label: el.data !== undefined && el.data !== null ? el.data.label : el.label,
        data: el.data !== undefined && el.data !== null ? { ...el.data } : {},
        draggable: el.draggable,
      });
      continue;
    }
    if (el.source !== undefined && el.target !== undefined) {
      edges.push({
        id: el.id,
        source: el.source,
        target: el.target,
        sourceHandle: el.sourceHandle,
        targetHandle: el.targetHandle,
        type: el.type,
        label: el.label,
        data: el.data !== undefined && el.data !== null ? { ...el.data } : {},
        markerEnd: el.markerEnd,
        style: el.style
      });
    }
  }
  return { nodes, edges };
}

function loadFlowData(flowData) {
  if (flowData === undefined || flowData === null) {
    elements.value = [];
    selectedCanvasNodeId.value = null;
    nextTick(() => fitView({ maxZoom: 1 }));
    return;
  }

  let normalizedFlow = null;
  let parsedFlowData = flowData;
  if (typeof flowData === 'string') {
    try {
      parsedFlowData = JSON.parse(flowData);
    } catch (error) {
      parsedFlowData = null;
    }
  }
  if (Array.isArray(parsedFlowData) === true) {
    const legacyNodes = parsedFlowData.filter(item => item !== null && item !== undefined && (item.position !== undefined || item.x !== undefined || item.y !== undefined));
    const legacyEdges = parsedFlowData.filter(item => item !== null && item !== undefined && item.source !== undefined && item.target !== undefined);
    normalizedFlow = { nodes: legacyNodes, edges: legacyEdges };
  } else {
    const objectFlowData = parsedFlowData !== null && parsedFlowData !== undefined && typeof parsedFlowData === 'object' ? parsedFlowData : {};
    if (Array.isArray(objectFlowData.elements) === true) {
      const legacyNodes = objectFlowData.elements.filter(item => item !== null && item !== undefined && (item.position !== undefined || item.x !== undefined || item.y !== undefined));
      const legacyEdges = objectFlowData.elements.filter(item => item !== null && item !== undefined && item.source !== undefined && item.target !== undefined);
      normalizedFlow = { nodes: legacyNodes, edges: legacyEdges };
    } else {
      const nodes = Array.isArray(objectFlowData.nodes) === true ? objectFlowData.nodes : [];
      const edges = Array.isArray(objectFlowData.edges) === true ? objectFlowData.edges : [];
      normalizedFlow = { nodes, edges };
    }
  }

  const nodesArr = [];
  const edgesArr = [];
  let maxNumericId = 0;

  for (let i = 0; i < normalizedFlow.nodes.length; i = i + 1) {
    const el = normalizedFlow.nodes[i];
    if (el !== undefined && el !== null && typeof el === 'object') {
      const fallbackPosition = {
        x: Number.isFinite(Number(el.x)) === true ? Number(el.x) : (100 + (i * 260)),
        y: Number.isFinite(Number(el.y)) === true ? Number(el.y) : 140
      };
      const resolvedPosition = el.position !== undefined && el.position !== null ? el.position : fallbackPosition;
      nodesArr.push({ ...el, position: resolvedPosition });
      if (typeof el.id === 'string' && el.id.startsWith('step-') === true) {
        const numPart = parseInt(el.id.split('-')[1], 10);
        if (isNaN(numPart) === false && numPart > maxNumericId) {
          maxNumericId = numPart;
        }
      }
    }
  }

  for (let i = 0; i < normalizedFlow.edges.length; i = i + 1) {
    const el = normalizedFlow.edges[i];
    if (el.source !== undefined && el.target !== undefined) {
      edgesArr.push({
        ...el,
        type: (el.type !== undefined && el.type !== null && el.type !== '') ? el.type : 'smoothstep',
        markerEnd: (el.markerEnd !== undefined && el.markerEnd !== null) ? el.markerEnd : { type: MarkerType.ArrowClosed, width: 20, height: 20, color: '#7c3aed' },
        style: (el.style !== undefined && el.style !== null) ? el.style : { stroke: '#7c3aed', strokeWidth: 2 },
      });
    }
  }

  id = maxNumericId + 1;

  elements.value = [...nodesArr, ...edgesArr];
  flowKey.value++;
  selectedCanvasNodeId.value = null;

  nextTick(() => {
    setNodes(nodesArr);
    setEdges(edgesArr);
    nextTick(() => fitView({ maxZoom: 1 }));
  });
}

async function loadWorkflowById(rawId) {
  const parsedId = Number.parseInt(String(rawId), 10);
  if (Number.isFinite(parsedId) !== true || parsedId <= 0) {
    return;
  }
  isLoadingWorkflow.value = true;
  try {
    const response = await axios.get(`${apiEndpoint.value}/api/workflows/${parsedId}`);
    const workflow = response !== undefined && response !== null ? response.data : null;
    if (workflow === null || workflow === undefined) {
      return;
    }
    const loadedIdRaw = workflow.workflowid !== undefined ? workflow.workflowid : workflow.workflowId;
    const loadedId = Number.parseInt(String(loadedIdRaw), 10);
    if (Number.isFinite(loadedId) === true && loadedId > 0) {
      workflowId.value = loadedId;
    }
    const loadedGuid = workflow.workflowguid !== undefined ? workflow.workflowguid : workflow.workflowGuid;
    if (loadedGuid !== undefined && loadedGuid !== null) {
      workflowGuid.value = String(loadedGuid);
    }
    workflowName.value = workflow.workflowname !== undefined ? String(workflow.workflowname) : String(workflow.workflowName !== undefined ? workflow.workflowName : '');
    const loadedFlowData = workflow.workflowdata !== undefined ? workflow.workflowdata : workflow.workflowData;
    loadFlowData(loadedFlowData);
  } catch (error) {
    toast.error('Failed to load workflow.');
  } finally {
    isLoadingWorkflow.value = false;
  }
}

async function saveWorkflow() {
  const trimmedName = workflowName.value.trim();
  if (trimmedName.length === 0) {
    toast.error('Workflow name is required.');
    return;
  }
  const flowData = getFlowData();
  if (Array.isArray(flowData.nodes) !== true || flowData.nodes.length === 0) {
    toast.error('Add at least one Trigger node before saving.');
    return;
  }
  const hasAnyTrigger = flowData.nodes.some(node => node.data !== undefined && node.data !== null && node.data.nodeType === 'Trigger');
  if (hasAnyTrigger !== true) {
    toast.error('Workflow must include at least one Trigger node.');
    return;
  }
  const triggerIds = new Set(
    flowData.nodes
      .filter(node => node !== undefined && node !== null && node.data !== undefined && node.data !== null && node.data.nodeType === 'Trigger')
      .map(node => String(node.id !== undefined && node.id !== null ? node.id : ''))
      .filter(nodeId => nodeId.length > 0)
  );
  const invalidIncomingTriggerEdge = flowData.edges.find(edge => {
    const targetId = String(edge !== undefined && edge !== null && edge.target !== undefined && edge.target !== null ? edge.target : '');
    return triggerIds.has(targetId) === true;
  });
  if (invalidIncomingTriggerEdge !== undefined && invalidIncomingTriggerEdge !== null) {
    toast.error('Trigger nodes cannot have incoming edges. Remove connection to trigger and save again.');
    return;
  }
  isSavingWorkflow.value = true;
  try {
    if (workflowId.value !== null) {
      const updateResponse = await axios.put(`${apiEndpoint.value}/api/workflows/${workflowId.value}`, {
        workflowName: trimmedName,
        workflowData: flowData,
        isActive: true
      });
      const updated = updateResponse !== undefined && updateResponse !== null ? updateResponse.data : null;
      if (updated !== null && updated !== undefined) {
        const updatedIdRaw = updated.workflowid !== undefined ? updated.workflowid : updated.workflowId;
        const updatedId = Number.parseInt(String(updatedIdRaw), 10);
        if (Number.isFinite(updatedId) === true && updatedId > 0) {
          workflowId.value = updatedId;
        }
        const updatedGuid = updated.workflowguid !== undefined ? updated.workflowguid : updated.workflowGuid;
        if (updatedGuid !== undefined && updatedGuid !== null) {
          workflowGuid.value = String(updatedGuid);
        }
      }
      toast.success('Workflow saved.');
      return;
    }
    const createResponse = await axios.post(`${apiEndpoint.value}/api/workflows/create`, {
      workflowName: trimmedName,
      workflowData: flowData
    });
    const created = createResponse !== undefined && createResponse !== null ? createResponse.data : null;
    if (created !== null && created !== undefined) {
      const createdIdRaw = created.workflowid !== undefined ? created.workflowid : created.workflowId;
      const createdId = Number.parseInt(String(createdIdRaw), 10);
      if (Number.isFinite(createdId) === true && createdId > 0) {
        workflowId.value = createdId;
      }
      const createdGuid = created.workflowguid !== undefined ? created.workflowguid : created.workflowGuid;
      if (createdGuid !== undefined && createdGuid !== null) {
        workflowGuid.value = String(createdGuid);
      }
      await router.replace({ path: '/workflowEditor', query: { id: String(workflowId.value) } });
    }
    toast.success('Workflow created.');
  } catch (error) {
    const errorText = typeof error?.response?.data?.error === 'string' ? error.response.data.error : 'Failed to save workflow.';
    toast.error(errorText);
  } finally {
    isSavingWorkflow.value = false;
  }
}

function getFirstTriggerTypeFromFlowData(flowData) {
  const nodes = Array.isArray(flowData?.nodes) === true ? flowData.nodes : [];
  for (let i = 0; i < nodes.length; i = i + 1) {
    const node = nodes[i];
    if (node !== undefined && node !== null && node.data !== undefined && node.data !== null && node.data.nodeType === 'Trigger') {
      const triggerType = String(node.data.triggerType !== undefined && node.data.triggerType !== null ? node.data.triggerType : '').trim();
      if (triggerType.length > 0) {
        return triggerType;
      }
    }
  }
  return '';
}

async function testWorkflow() {
  if (workflowId.value === null) {
    toast.error('Save workflow first before testing.');
    return;
  }
  const flowData = getFlowData();
  const triggerType = getFirstTriggerTypeFromFlowData(flowData);
  if (triggerType.length === 0) {
    toast.error('Workflow must include a Trigger node to test.');
    return;
  }
  isTestingWorkflow.value = true;
  try {
    const response = await axios.post(`${apiEndpoint.value}/api/workflows/${workflowId.value}/run`, {
      triggerType,
      payload: {}
    });
    const runIdValue = response !== undefined && response !== null && response.data !== undefined && response.data !== null ? response.data.runId : '';
    const runId = String(runIdValue !== undefined && runIdValue !== null ? runIdValue : '').trim();
    if (runId.length > 0) {
      toast.success(`Test flow executed (${triggerType}) runId: ${runId}`);
      return;
    }
    toast.success(`Test flow executed (${triggerType}).`);
  } catch (error) {
    const errorText = typeof error?.response?.data?.error === 'string' ? error.response.data.error : 'Failed to test workflow.';
    toast.error(errorText);
  } finally {
    isTestingWorkflow.value = false;
  }
}

defineExpose({
  getFlowData,
  loadFlowData,
  fitView
});

onMounted(async () => {
  isLoadingWorkflow.value = true;
  try {
    await Promise.all([
      fetchAvailableTools(),
      fetchAvailableAgents(),
      fetchAvailableTags()
    ]);
  } catch (error) {
    console.error('Error fetching initial workflow data', error);
  }

  const rawId = route.query !== undefined && route.query !== null ? route.query.id : null;
  if (rawId !== undefined && rawId !== null && String(rawId).trim().length > 0) {
    await loadWorkflowById(rawId);
  } else {
    loadFlowData({ nodes: [], edges: [] });
    isLoadingWorkflow.value = false;
  }
  fitView({ maxZoom: 1 });
});

function closeStepModal() {
  isStepModalOpen.value = false;
  editingNodeId.value = null;
}

function closeActionStepModal() {
  isActionStepModalOpen.value = false;
  editingNodeId.value = null;
}

function saveStepData(updatedData) {
  if (editingNodeId.value !== null) {
    const nodeToUpdate = findNode(editingNodeId.value);
    if (nodeToUpdate !== undefined && nodeToUpdate !== null) {
      
      elements.value = elements.value.map(el => {
        if (el.id === editingNodeId.value) {
          const newLabel = (updatedData.label !== undefined && updatedData.label !== null && updatedData.label.trim().length > 0) ? updatedData.label : `Step ${el.id}`;
          return {
            ...el,
            label: newLabel,
            data: { 
              ...el.data, 
              ...updatedData, 
              label: newLabel 
            },
          };
        }
        return el;
      });
    }
  }
  closeStepModal();
  closeActionStepModal();
}

function organizeFlow() {
    const nodes = elements.value.filter(el => el.position !== undefined && el.position !== null);
    const edges = elements.value.filter(el => el.source !== undefined && el.source !== null);

    const triggerNode = nodes.find(n => n.data !== undefined && n.data !== null && n.data.nodeType === 'Trigger');
    const firstNode = nodes.length > 0 ? nodes[0] : null;
    const rootNode = triggerNode !== undefined && triggerNode !== null ? triggerNode : firstNode;
    if (rootNode === undefined || rootNode === null) {
        return;
    }

    const adj = new Map();
    for (let i = 0; i < nodes.length; i = i + 1) {
        adj.set(nodes[i].id, []);
    }
    for (let i = 0; i < edges.length; i = i + 1) {
        const edge = edges[i];
        if (adj.has(edge.source) === true) {
            adj.get(edge.source).push(edge.target);
        }
    }

    const xSpacing = 300;
    const ySpacing = 150;
    const nodeHeightEstimate = 100;

    const newPositions = new Map();
    const levels = new Map();
    const nodesAtLevel = new Map();

    const queue = [{ nodeId: rootNode.id, level: 0 }];
    const visited = new Set([rootNode.id]);
    levels.set(rootNode.id, 0);

    if (nodesAtLevel.has(0) === false) {
        nodesAtLevel.set(0, []);
    }
    nodesAtLevel.get(0).push(rootNode.id);

    let head = 0;
    while(head < queue.length) {
        const item = queue[head];
        head = head + 1;
        const nodeId = item.nodeId;
        const level = item.level;
        const children = adj.get(nodeId) !== undefined && adj.get(nodeId) !== null ? adj.get(nodeId) : [];
        for (let i = 0; i < children.length; i = i + 1) {
            const childId = children[i];
            const childNode = findNode(childId);
            if (childNode !== undefined && childNode !== null && visited.has(childId) === false) {
                visited.add(childId);
                levels.set(childId, level + 1);
                if (nodesAtLevel.has(level + 1) === false) {
                    nodesAtLevel.set(level + 1, []);
                }
                nodesAtLevel.get(level + 1).push(childId);
                queue.push({ nodeId: childId, level: level + 1 });
            }
        }
    }
    
    const canvasElement = document.querySelector('.wf-canvas');
    const canvasCenterY = canvasElement !== null ? canvasElement.clientHeight / 2 : 300;

    nodesAtLevel.forEach((nodesInLevel, level) => {
        const x = 100 + level * xSpacing;
        const numNodes = nodesInLevel.length;
        const levelHeight = numNodes * nodeHeightEstimate + Math.max(0, numNodes - 1) * ySpacing;
        let currentY = canvasCenterY - levelHeight / 2;

        for (let i = 0; i < nodesInLevel.length; i = i + 1) {
            const nodeId = nodesInLevel[i];
            newPositions.set(nodeId, { x, y: currentY + nodeHeightEstimate / 2 });
            currentY = currentY + nodeHeightEstimate + ySpacing;
        }
    });

    elements.value = elements.value.map(el => {
        if (el.position !== undefined && el.position !== null && newPositions.has(el.id) === true) {
            return { ...el, position: newPositions.get(el.id) };
        }
        return el;
    });

    nextTick(() => {
        fitView({ maxZoom: 1 });
    });
}

</script>

<style scoped>
.wf-editor {
  height: calc(100vh - 170px);
  min-height: 520px;
  width: 100%;
  display: flex;
  flex-direction: column;
  outline: none;
  position: relative;
  background: #f3f4f6;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  direction: ltr !important;
}

.wf-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 24px;
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  z-index: 10;
}

.wf-header-left {
  display: flex;
  align-items: center;
}

.wf-header-left > * + * { margin-left: 16px; }
.wf-header-right > * + * { margin-left: 16px; }

.wf-name-input {
  width: 280px;
  padding: 8px 12px;
  font-size: 0.95rem;
  font-weight: 500;
  background: #f9fafb;
  border: 1px solid transparent;
  border-radius: 6px;
  color: #111827;
  transition: all 0.2s ease;
}

.wf-name-input:hover {
  background: #f3f4f6;
}

.wf-name-input:focus {
  background: #ffffff;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
  outline: none;
}

.wf-name-input::placeholder {
  color: #9ca3af;
  font-weight: 400;
}

.wf-btn {
  display: inline-flex;
  align-items: center;
  padding: 8px 16px;
  font-size: 0.875rem;
  font-weight: 500;
  border: 1px solid transparent;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.wf-btn > svg {
  margin-right: 6px;
}

.wf-btn-ghost {
  background: transparent;
  color: #4b5563;
}

.wf-btn-ghost:hover {
  background: #f3f4f6;
  color: #111827;
}

.wf-btn-danger {
  background: #fef2f2;
  color: #dc2626;
}

.wf-btn-danger:hover {
  background: #fee2e2;
  color: #b91c1c;
}

.wf-body {
  flex: 1;
  min-height: 0;
  display: flex;
  position: relative;
}

.wf-editor-loading {
  position: absolute;
  inset: 0;
  z-index: 50;
  background: rgba(255, 255, 255, 0.84);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
}

.wf-editor-loading-spinner {
  width: 54px;
  height: 54px;
  animation: wf-editor-spin 1s linear infinite;
}

.wf-editor-loading-track {
  stroke: #e5e7eb;
}

.wf-editor-loading-head {
  stroke: #4f46e5;
  stroke-linecap: round;
  stroke-dasharray: 90 150;
  stroke-dashoffset: 0;
}

.wf-editor-loading-label {
  margin-top: 12px;
  color: #374151;
  font-size: 0.9rem;
  font-weight: 600;
}

@keyframes wf-editor-spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.wf-sidebar {
  width: 320px;
  background: #ffffff;
  border-left: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: -1px 0 2px 0 rgba(0, 0, 0, 0.02);
  z-index: 5;
  order: 2;
}

.wf-sidebar-header {
  padding: 16px 20px;
  border-bottom: 1px solid #f3f4f6;
  background: #ffffff;
}

.wf-sidebar-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.wf-sidebar-title {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.wf-sidebar-header-actions {
  display: flex;
  align-items: center;
  background: #f3f4f6;
  border-radius: 6px;
  padding: 3px;
}

.wf-sidebar-mode-btn {
  background: transparent;
  border: none;
  border-radius: 4px;
  color: #6b7280;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 4px 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 60px;
}

.wf-sidebar-mode-btn:hover:not(.wf-sidebar-mode-btn-active) {
  color: #374151;
}

.wf-sidebar-mode-btn-active {
  background: #ffffff;
  color: #111827;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.wf-sidebar-main {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.wf-sidebar-search-wrap {
  padding: 12px 16px 0 16px;
}

.wf-sidebar-search-input {
  width: 100%;
}

.wf-sidebar-list {
  padding: 16px;
  overflow: auto;
  min-height: 0;
  flex: 1;
}

.wf-sidebar-group {
  margin-bottom: 24px;
}

.wf-sidebar-group:last-child {
  margin-bottom: 0;
}

.wf-sidebar-group-title {
  font-size: 0.7rem;
  color: #9ca3af;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 12px;
  padding-left: 2px;
}

.wf-sidebar-item {
  width: 100%;
  padding: 12px;
  margin-bottom: 8px;
  text-align: left;
  display: flex;
  flex-direction: column;
  cursor: pointer;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.wf-sidebar-item:hover {
  border-color: #6366f1;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
  transform: translateY(-1px);
}

.wf-sidebar-item:active {
  transform: translateY(0);
}

.wf-sidebar-item:last-child {
  margin-bottom: 0;
}

.wf-sidebar-item-main {
  display: flex;
  align-items: flex-start;
}

.wf-sidebar-item-main > * + * { margin-left: 12px; }

.wf-sidebar-item-img {
  width: 24px;
  height: 24px;
  object-fit: contain;
  flex-shrink: 0;
  border-radius: 4px;
}

.wf-sidebar-item-text {
  display: flex;
  flex-direction: column;
}

.wf-sidebar-item-label {
  font-size: 0.875rem;
  color: #111827;
  font-weight: 600;
}

.wf-sidebar-item-desc {
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 4px;
  line-height: 1.4;
}

.wf-sidebar-config {
  display: none;
  border-top: 1px solid #e5e7eb;
  padding: 20px;
  overflow: auto;
  max-height: 45vh;
  background: #f9fafb;
}

.wf-sidebar-config-title {
  font-size: 0.75rem;
  font-weight: 600;
  color: #4b5563;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 16px;
}

.wf-config-row {
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
}

.wf-config-row:last-child {
  margin-bottom: 0;
}

.wf-config-row label {
  font-size: 0.75rem;
  color: #374151;
  margin-bottom: 6px;
  font-weight: 500;
}

.wf-input,
.wf-select {
  padding: 8px 12px;
  font-size: 0.875rem;
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  color: #111827;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
}

.wf-select,
.node-inline-select {
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='%236b7280'%3E%3Cpath fill-rule='evenodd' d='M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.118l3.71-3.89a.75.75 0 1 1 1.08 1.04l-4.25 4.46a.75.75 0 0 1-1.08 0l-4.25-4.46a.75.75 0 0 1 .02-1.06Z' clip-rule='evenodd' /%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-size: 16px;
  background-position: right 10px center;
  padding-right: 34px;
}

.wf-agent-multiselect-btn {
  min-height: 36px;
}

.wf-input:focus,
.wf-select:focus,
.node-inline-input:focus,
.node-inline-select:focus,
.node-inline-textarea:focus,
.wf-chat-input:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.2);
}

.wf-textarea {
  padding: 10px 12px;
  font-size: 0.875rem;
  min-height: 100px;
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  color: #111827;
  resize: vertical;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
}

.wf-canvas-wrap {
  flex: 1;
  min-width: 0;
  min-height: 0;
  position: relative;
}

.wf-canvas {
  flex-grow: 1;
  min-width: 0;
  min-height: 0;
  background: #f3f4f6;
  background-image: radial-gradient(#d1d5db 1px, transparent 1px);
  background-size: 20px 20px;
}

.edge-condition-card {
  background: #ffffff;
  padding: 4px 10px;
  border-radius: 6px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
  font-size: 0.7rem;
  border: 1px solid #e5e7eb;
  cursor: pointer;
  white-space: nowrap;
  color: #374151;
  font-weight: 600;
  transition: all 0.2s;
}

.edge-condition-card:hover {
  border-color: #6366f1;
  color: #4f46e5;
  transform: translateY(-50%) translateY(-1px);
}

.edge-condition-card.no-condition {
  color: #9ca3af;
  border-style: dashed;
  background: rgba(255, 255, 255, 0.9);
}

:deep(.vue-flow__node) {
  overflow: visible !important;
}

:deep(.vue-flow__node-default) {
  border-radius: 12px;
  background: #ffffff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.02);
  border: 1px solid #e5e7eb;
  min-width: 280px;
  max-width: 340px;
  transition: box-shadow 0.2s ease, border-color 0.2s ease, transform 0.2s ease;
  overflow: visible !important;
}

:deep(.vue-flow__node-default:hover) {
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.06), 0 2px 4px rgba(0, 0, 0, 0.04);
  transform: translateY(-2px);
}

:deep(.vue-flow__node-default.trigger-node) {
  border-color: #e5e7eb;
}

:deep(.vue-flow__node-default.logic-node) {
  border-color: #e5e7eb;
}

:deep(.vue-flow__node-default.action-node) {
  border-color: #e5e7eb;
}

:deep(.vue-flow__node-root) {
  border-radius: 12px;
  background: #ffffff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.02);
  border: 1px solid #e5e7eb;
  overflow: visible !important;
}

.node-card {
  position: relative;
  display: flex;
  flex-direction: column;
}

.node-id-badge {
  position: absolute;
  top: 8px;
  left: 10px;
  font-size: 0.68rem;
  color: #9ca3af;
  font-weight: 600;
  letter-spacing: 0.01em;
  z-index: 2;
  pointer-events: none;
}

.node-header {
  display: flex;
  align-items: center;
  padding: 20px 20px 0 20px;
  color: #111827;
  background: #ffffff;
  border-radius: 11px 11px 0 0;
}

.header-trigger { border-top: 0; }
.header-if { border-top: 0; }
.header-action { border-top: 0; }
.header- { border-top: 0; }
.header-root { border-top: 0; }

.node-header-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-inline-end: 12px;
  padding: 0;
  border-radius: 0;
  background: transparent !important;
}

.header-trigger .node-header-icon { color: #8b5cf6; }
.header-if .node-header-icon { color: #0ea5e9; }
.header-action .node-header-icon { color: #10b981; }
.header-root .node-header-icon { color: #f59e0b; }
.header- .node-header-icon { color: #64748b; }

.node-header-title-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.node-header-subtitle {
  font-size: 0.7rem;
  font-weight: 700;
  color: #8b5cf6;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 4px;
}

.header-if .node-header-subtitle { color: #0ea5e9; }
.header-action .node-header-subtitle { color: #10b981; }
.header-root .node-header-subtitle { color: #f59e0b; }
.header- .node-header-subtitle { color: #64748b; }

.node-header-title {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  font-size: 1.15rem;
  font-weight: 800;
  color: #1f2937;
  text-transform: none;
  letter-spacing: -0.01em;
  line-height: 1.2;
}

.node-body {
  padding: 20px;
  padding-bottom: 16px;
  background: #ffffff;
  display: flex;
  flex-direction: column;
}

.node-body.has-in-port {
  padding-left: 24px;
}

.monaco-container-wrapper {
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  overflow: hidden;
}

.node-title-area {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 16px;
  border-bottom: 1px solid #f3f4f6;
  padding-top: 8px;
  padding-bottom: 12px;
}

.node-icon-and-type {
  display: inline-flex;
  align-items: center;
  align-self: center;
  padding: 4px 8px;
  border-radius: 6px;
  background: #f3f4f6;
  color: #6b7280;
  margin-bottom: 8px;
}

.trigger-node .node-icon-and-type {
  background: #ede9fe;
  color: #7c3aed;
}

.action-node .node-icon-and-type {
  background: #d1fae5;
  color: #059669;
}

.logic-node .node-icon-and-type {
  background: #e0f2fe;
  color: #0284c7;
}

.node-type-label {
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-left: 6px;
}

.node-main-title {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  font-size: 1.15rem;
  font-weight: 800;
  color: #1f2937;
  line-height: 1.2;
  text-align: center;
}

.node-type-tag {
  display: none;
}

.custom-node-padding {
  padding: 0;
}

.custom-node-padding.has-in-port .node-type-tag {
  margin-top: 0;
}

.node-inline-row {
  margin-top: 12px;
}

.node-inline-row:first-child {
  margin-top: 0;
}

.node-inline-label {
  color: #4b5563;
  font-size: 0.75rem;
  font-weight: 600;
  margin-bottom: 4px;
  display: block;
}

.node-inline-hint {
  color: #6b7280;
  font-size: 0.68rem;
  margin-top: 6px;
}

.node-inline-input,
.node-inline-select {
  width: 100%;
  min-height: 36px;
  padding: 6px 10px;
  font-size: 0.85rem;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  color: #111827;
  transition: all 0.2s ease;
}

.node-inline-input:hover,
.node-inline-select:hover {
  background: #ffffff;
  border-color: #d1d5db;
}

.node-inline-input:focus,
.node-inline-select:focus {
  background: #ffffff;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
  outline: none;
}

.node-inline-textarea {
  width: 100%;
  min-height: 60px;
  padding: 8px 10px;
  font-size: 0.85rem;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  color: #111827;
  resize: vertical;
  transition: all 0.2s ease;
}

.node-inline-textarea:hover {
  background: #ffffff;
  border-color: #d1d5db;
}

.port-in-row {
  left: 0;
  top: 0;
  bottom: 0;
  width: 0;
  right: auto;
}

.custom-top-handle {
  display: none;
}

.custom-left-handle {
  position: absolute !important;
  left: -7px !important;
  top: 50% !important;
  transform: translateY(-50%);
  width: 14px !important;
  height: 14px !important;
}

.port-out-row {
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 0;
  padding-top: 10px;
  margin-top: 20px;
  margin-bottom: 4px;
  border-top: 1px solid #f3f4f6;
  background: transparent;
}

.port-out-item {
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  min-height: 24px;
}

.port-out-item + .port-out-item {
  margin-top: 12px;
}

.if-line-port + .if-line-port {
  margin-top: 12px;
}

.port-label-bottom {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  font-size: 0.75rem;
  color: #4b5563;
  font-weight: 700;
  margin-right: 16px;
  line-height: 1;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.custom-bottom-handle {
  display: none;
}

.custom-right-handle {
  position: absolute !important;
  right: -7px !important;
  transform: translateY(-50%);
  width: 14px !important;
  height: 14px !important;
}

:deep(.vue-flow__handle) {
  background: #ffffff;
  border: 3px solid #6366f1;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  width: 14px;
  height: 14px;
}

:deep(.vue-flow__handle:hover) {
  background: #e0e7ff;
  border-color: #4f46e5;
  transform: translateY(-50%) scale(1.25);
  box-shadow: 0 4px 6px rgba(99, 102, 241, 0.2);
}

:deep(.vue-flow__node.selected),
:deep(.vue-flow__node-default.selected),
:deep(.vue-flow__node-Root.selected),
:deep(.vue-flow__node-Question.selected),
:deep(.vue-flow__node-Action.selected),
:deep(.vue-flow__node-KnowledgeBase.selected) {
  border-color: #6366f1 !important;
  box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.4), 0 4px 6px -1px rgba(0, 0, 0, 0.1) !important;
}

:deep(.vue-flow__controls) {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

:deep(.vue-flow__controls button) {
  color: #4b5563;
  border-bottom: 1px solid #f3f4f6;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

:deep(.vue-flow__controls button:last-child) {
  border-bottom: none;
}

:deep(.vue-flow__controls button:hover) {
  background: #f9fafb;
  color: #111827;
}

:deep(.vue-flow__edge-path) {
  stroke: #94a3b8;
  stroke-width: 2;
  transition: stroke 0.2s ease, stroke-width 0.2s ease;
}

:deep(.vue-flow__edge:hover .vue-flow__edge-path),
:deep(.vue-flow__edge.selected .vue-flow__edge-path) {
  stroke: #6366f1;
  stroke-width: 2.5;
}

:deep(.vue-flow__background circle),
:deep(.vue-flow__background line) {
  stroke: #e5e7eb;
}

.wf-chat-panel {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: #ffffff;
}

.wf-chat-panel-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 16px;
  display: flex;
  flex-direction: column;
}

.wf-chat-panel-content > * + * {
  margin-top: 12px;
}

.wf-chat-panel-loading {
  min-height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.wf-chat-empty {
  margin-top: auto;
  margin-bottom: auto;
  text-align: center;
  padding: 20px;
}

.wf-chat-empty-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
}

.wf-chat-empty-subtitle {
  font-size: 0.8rem;
  color: #6b7280;
  line-height: 1.5;
}

.wf-chat-bubble {
  max-width: 90%;
  padding: 12px 14px;
  border-radius: 12px;
  font-size: 0.85rem;
  line-height: 1.5;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
}

.wf-chat-bubble-user {
  align-self: flex-end;
  background: #4f46e5;
  color: #ffffff;
  border-bottom-right-radius: 4px;
}

.wf-chat-bubble-assistant {
  align-self: flex-start;
  background: #f9fafb;
  color: #111827;
  border: 1px solid #e5e7eb;
  border-bottom-left-radius: 4px;
}

.wf-chat-bubble-content :deep(p) {
  margin: 0;
}

.wf-chat-typing-dots {
  color: #9ca3af;
}

.wf-chat-panel-input-area {
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
  padding: 12px 16px;
}

.wf-chat-quick-prompts {
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 10px;
  row-gap: 8px;
}

.wf-chat-quick-prompts > * + * {
  margin-left: 8px;
}

.wf-chat-quick-prompt {
  border: 1px solid #e5e7eb;
  background: #ffffff;
  color: #374151;
  border-radius: 999px;
  padding: 5px 10px;
  font-size: 0.7rem;
  line-height: 1.2;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.wf-chat-quick-prompt:hover {
  border-color: #6366f1;
  color: #4338ca;
  background: #eef2ff;
}

.wf-chat-panel-input-row {
  display: flex;
  align-items: flex-end;
}

.wf-chat-panel-input-row > * + * {
  margin-left: 10px;
}

.wf-chat-panel-input {
  flex: 1;
  min-width: 0;
  min-height: 44px;
  max-height: 140px;
  resize: none;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.85rem;
  line-height: 1.4;
  color: #111827;
  padding: 10px 12px;
  background: #ffffff;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
}

.wf-chat-panel-input:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.2);
}

.wf-chat-panel-send {
  width: 44px;
  height: 44px;
  border: none;
  border-radius: 8px;
  background: #4f46e5;
  color: #ffffff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px 0 rgba(79, 70, 229, 0.3);
}

.wf-chat-panel-send:hover {
  background: #4338ca;
  transform: translateY(-1px);
  box-shadow: 0 4px 6px -1px rgba(79, 70, 229, 0.2), 0 2px 4px -1px rgba(79, 70, 229, 0.1);
}

.wf-chat-panel-send:active {
  transform: translateY(0);
}
</style> 
